import { 
  setupClass, 
  makeEnum,
  clamp,
} from '../Base.js'

// Colors for stacked bar charts
const SENSIBLE_COLOR = '#E74C3C'; // More red color for sensible loads
const LATENT_COLOR = '#4285F4';   // Google Blue for latent loads

// Below this, m_return is considered too close to zero to divide by safely.
const M_RETURN_EPSILON = 0.002;

import {
  valOr,
  formatNum,
  prettyJson,
  deepCopyObject,
  getMonthHourString,
  getShortMonthName,
  getHourString,
  safeDivide,
} from '../SharedUtils.js'
import { IsTestEnv } from '../Globals.js'

import { Field, FieldType, } from '../Common/Field.js'
import { Units, getLabel } from '../Common/Units.js'
import { CalcContext } from '../Common/CalcContext.js'

import {
  SystemType,
  ZoneAirflowCalcMethod,
} from './System.js'
import * as Space from './Space.js'
import { SpaceCoolingResultsNode } from './SpaceCoolingCalculator.js';
import { RecoveryType, } from '../Components/HouseVentilationInfiltration.js'
import { YesNo } from '../Components/Common.js'

import { DataCache } from '../MaterialData/DataCache.js'
import * as MaterialDataTableRegistry from '../MaterialData/MaterialDataTableRegistry.js'

import { WorkerPool, MockWorkerPool, } from '../Common/WorkerPool.js'
import SpaceWorkerURL from '../workers/SpaceWorker?url&worker'

import * as math from 'mathjs'
import {
  MatrixUtils, scalarSum, makeVector, makeMonthVector, makeHourVector,
  clampToMax, clampToMin,
} from '../Common/Math.js'

import { Quantity } from '../Common/Quantity.js'
import { OutputValue } from '../Common/OutputValue.js'

import * as psy from '../Components/Psychrometrics.js'
import { PsyCalcMethod } from '../Components/Psychrometrics.js'
import { findMaxLoadInMatrix, findMaxValueInArray, findMaxLoadForMonth, } from './LoadUtils.js'

import {
  ResultsNode,
  ResultsNodeType,
  LineChartNode,
  MultiLineChartNode,
  StackedBarChartNode,
  SunburstNode,
  SummaryTableNode,
  MonthHourGridNode,
  PsychrometricsChartNode,
} from './ResultsTree.js'

let RegularOrMin = makeEnum({
  Regular: 'Regular',
  Min: 'Min',
})

let HeatingOrCooling = makeEnum({
  Heating: 'Heating',
  Cooling: 'Cooling',
})

function applySpaceQuantities(matrixArr, spaceQuantities) {
  let res = []
  for (let i = 0; i < matrixArr.length; ++i) {
    res.push(math.multiply(matrixArr[i], spaceQuantities[i]))
  }
  return res
}

class MonthHourMap {
  constructor() {
    this.matrix = {};
    for (let mo = 0; mo < 12; ++mo) {
      this.matrix[mo] = {};
      for (let hr = 0; hr < 24; ++hr) {
        this.matrix[mo][hr] = null;
      }
    }
  }

  get([mo, hr]) {
    return this.matrix[mo][hr]
  }

  set([mo, hr], newValue) {
    this.matrix[mo][hr] = newValue
  }

  toValString() {
    return `[12 x 24 Month-Hour Map]`
  }
}

class QSupplyValues {
  constructor(q_supply_values) {
    this.q_supply_values = q_supply_values
  }

  getSystemSupplyCooling(mo, hr) {
    return this.q_supply_values.Q_supply_cooling.get([mo, hr])
  }

  findPeakSystemSupplyCooling() {
    return findMaxLoadInMatrix(this.q_supply_values.Q_supply_cooling)
  }

  getSystemSupplyHeating() {
    return this.q_supply_values.Q_supply_heating
  }

  getZoneSupplyCooling(zoneIndex, mo, hr) {
    return this.q_supply_values.Q_zone_supply_cooling_values[zoneIndex].get([mo, hr])
  }

  getZoneSupplyHeating(zoneIndex) {
    return this.q_supply_values.Q_zone_supply_heating_values[zoneIndex]
  }

  getZoneSupplyCoolingPeak(zoneIndex) {
    return findMaxLoadInMatrix(this.q_supply_values.Q_zone_supply_cooling_values[zoneIndex]).value;
  }

  getSumOfZoneCoolingAirflows(mo, hr) {
    let sum = 0;
    for (const zoneSupplyCooling of this.q_supply_values.Q_zone_supply_cooling_values) {
      sum += zoneSupplyCooling.get([mo, hr])
    }
    return sum;
  }

  getSpaceSupplyCooling(zoneIndex, spaceIndex, mo, hr) {
    return this.q_supply_values.Q_space_supply_cooling_values[zoneIndex][spaceIndex].get([mo, hr])
  }

  getSpaceSupplyCoolingPeak(zoneIndex, spaceIndex) {
    let peakInfo = findMaxLoadInMatrix(this.q_supply_values.Q_space_supply_cooling_values[zoneIndex][spaceIndex])
    return peakInfo.value;
  }

  getSpaceSupplyHeating(zoneIndex, spaceIndex) {
    return this.q_supply_values.Q_space_supply_heating_values[zoneIndex][spaceIndex]
  }
}

class SpaceVentilationValues {
  constructor() {
    this.spaceIdToValues = {}
  }

  setValuesForSpaceType(spaceType, values) {
    this.spaceIdToValues[spaceType.id] = values
  }

  getValuesForSpaceType(spaceType) {
    return this.spaceIdToValues[spaceType.id]
  }

  hasValuesForSpaceType(spaceType) {
    return this.spaceIdToValues.hasOwnProperty(spaceType.id)
  }
}

class OutdoorAirIntakeValues {
  constructor(values) {
    this.values = values
  }

  get_V_ot_cooling_for_hr(hr) {
    return this.values.V_ot_cooling_by_hr[hr]
  }

  get_V_ot_cooling_max() {
    return findMaxValueInArray(this.values.V_ot_cooling_by_hr).value;
  }

  get_V_ot_cooling_matrix() {
    let m = math.zeros(12, 24)
    for (let mo = 0; mo < 12; ++mo) {
      for (let hr = 0; hr < 24; ++hr) {
        m.set([mo, hr], this.values.V_ot_cooling_by_hr[hr])
      }
    }
    return m
  }

  get_V_ot_heating() {
    return this.values.V_ot_heating
  }

  get_D_cooling() {
    return this.values.D_cooling;
  }

  get_E_v_cooling() {
    return this.values.E_v_cooling;
  }

  get_V_ou_cooling() {
    return this.values.V_ou_cooling;
  }
}

class SpaceResult {
  constructor(zoneSpace) {
    this.zoneSpace = zoneSpace
    this.loadResults = null;
    this.calcLog = null;
  }

  // We convert 'outputs' with zoneResults to JSON using
  // JSON.stringify for tests, so implement this here
  toJSON() {
    return {
      zoneSpaceName: this.zoneSpace.getSpaceName(),
      zoneSpaceQuantity: this.zoneSpace.getQuantity(),
      loadResults: this.loadResults,
    }
  }

  getLoadResults() {
    return this.loadResults
  }

  setLoadResults(loadResults) {
    this.loadResults = loadResults
  }

  getCalcLog() {
    return this.calcLog
  }

  setCalcLog(calcLog) {
    this.calcLog = calcLog
  }
}

class ZoneResult {
  constructor(zone, numSpaces) {
    this.zoneName = zone.getName()
    this.spaceResults = new Array(numSpaces).fill(null)
    this.totals = null
  }

  getSpaceResult(spaceIndex) {
    return this.spaceResults[spaceIndex]
  }

  setSpaceResult(spaceIndex, spaceResult) {
    this.spaceResults[spaceIndex] = spaceResult
  }

  getSpaceLoadResults(spaceIndex) {
    return this.spaceResults[spaceIndex].getLoadResults()
  }

  calculateZoneTotals(ctx) {
    ctx.startSection(`Calc zone totals - Zone ${this.zoneName}`)
    
    ctx.cooling_sensible = math.zeros(12, 24)
    ctx.cooling_latent = math.zeros(12, 24)
    ctx.cooling_total = math.zeros(12, 24)
    // Note - there are no latent heating loads
    ctx.heating_sensible = 0
    ctx.heating_total = 0
    for (const spaceResult of this.spaceResults) {
      let zoneSpace = spaceResult.zoneSpace
      ctx.startLocalSection(`Space ${zoneSpace.getSpaceName()} (x${zoneSpace.getQuantity()})`)
      let loadResults = spaceResult.getLoadResults()
      ctx.cooling_sensible = ctx.eval('cooling_sensible + N*space_cooling_sensible', {
        N: zoneSpace.getQuantity(),
        space_cooling_sensible: loadResults.cooling.q_sensible,
      }, 'cooling_sensible')
      ctx.cooling_latent = ctx.eval('cooling_latent + N*space_cooling_latent', {
        N: zoneSpace.getQuantity(),
        space_cooling_latent: loadResults.cooling.q_latent,
      }, 'cooling_latent')
      ctx.heating_sensible = ctx.eval('heating_sensible + N*space_heating_sensible', {
        N: zoneSpace.getQuantity(),
        space_heating_sensible: loadResults.heating.q_sensible,
      }, 'heating_sensible')
      ctx.endSection()
    }
    ctx.cooling_total = ctx.eval('cooling_sensible + cooling_latent', {}, 'cooling_total')
    ctx.heating_total = ctx.eval('heating_sensible', {}, 'heating_total')

    ctx.log("Zone totals:")
    ctx.logLoadMatrix('Cooling sensible', ctx.cooling_sensible)
    ctx.logLoadMatrix('Cooling latent', ctx.cooling_latent)
    ctx.logLoadMatrix('Cooling total', ctx.cooling_total)
    ctx.logValue('Heating sensible', ctx.heating_sensible)
    ctx.logValue('Heating total', ctx.heating_total)

    this.totals = {
      cooling: {
        q_sensible: ctx.cooling_sensible,
        q_latent: ctx.cooling_latent,
        q_total: ctx.cooling_total,
      },
      heating: {
        q_sensible: ctx.heating_sensible,
        q_total: ctx.heating_total,
      }
    }
    ctx.endSection()
  }

  getZoneTotals() {
    return this.totals
  }
}

class ZoneResults {
  constructor(numZones) {
    this.zoneResults = new Array(numZones).fill(null)
  }

  getZoneResult(zoneIndex) {
    return this.zoneResults[zoneIndex]
  }

  getSpaceResult(zoneIndex, spaceIndex) {
    return this.getZoneResult(zoneIndex).getSpaceResult(spaceIndex)
  }

  setZoneResult(zoneIndex, zoneResult) {
    this.zoneResults[zoneIndex] = zoneResult
  }

  calculateZoneTotals(ctx) {
    ctx.startSection("Calculate Zone totals")
    for (const zoneResult of this.zoneResults) {
      zoneResult.calculateZoneTotals(ctx)
    }
    ctx.endSection()
  }
}

export class SystemLoadCalculator {
  constructor(system, proj, ctx, debugOpts) {
    this.system = system
    this.proj = proj;
    this.ctx = ctx
    this.debugOpts = valOr(debugOpts, {})

    this.zoneResults = new ZoneResults(system.zones.length)
  }

  calc_T_db_out_heating() {
    return this.ctx.designTemps.getHeatingDryBulbOut()
  }

  calc_T_db_out_cooling(hr, mo) {
    return this.ctx.designTemps.getCoolingDryBulbOut(this.ctx, mo, hr)
  }

  calc_T_wb_out_cooling(hr, mo) {
    return this.ctx.designTemps.getCoolingWetBulbOut(this.ctx, mo, hr)
  }

  calc_V_ou_multi_zone(regularOrMin, diversityFactor) {
    let ctx = this.ctx
    ctx.startSection(`Calc V_ou multizone - ${regularOrMin}`)
    ctx.D = diversityFactor

    ctx.V_ou_sum = 0
    this.system.forEachSpace((space) => {
      let spaceType = space.spaceType;
      ctx.startLocalSection(`Space ${spaceType.getName()}`)
      if (spaceType.ventilation.usesManualInput()) {
        ctx.log("Using manual ventilation values")
        ctx.V_values = spaceType.ventilation.getManualVentilationValues(ctx)
        if (regularOrMin == RegularOrMin.Regular) {
          ctx.V_ou = ctx.eval("V_bz", {
            V_bz: ctx.V_values.V_bz,
          }, 'V_ou')
        } else {
          ctx.V_ou = ctx.eval("V_bz_min", {
            V_bz_min: ctx.V_values.V_bz_min,
          }, 'V_ou')
        }
      } else {
        ctx.log("Using automatic ventilation values")
        ctx.V_values = spaceType.ventilation.getAutomaticVentilationValues(ctx)
        if (regularOrMin == RegularOrMin.Regular) {
          ctx.log("Calculating regular/occupied ventilation")
          ctx.V_ou = ctx.eval("D*P_space*R_p_space + A_space*R_a_space", {
            P_space: spaceType.getNumOccupants(),
            R_p_space: ctx.V_values.cfm_per_person,
            A_space: spaceType.floorArea.value,
            R_a_space: ctx.V_values.cfm_per_ft2,
          }, 'V_ou')
        } else {
          ctx.log("Calculating minimum ventilation")
          ctx.V_ou = ctx.eval("0 + A_space*R_a_space_min", {
            A_space: spaceType.floorArea.value,
            R_a_space_min: ctx.V_values.cfm_per_ft2,
          }, 'V_ou')
        }
      }
      ctx.V_ou_sum = ctx.eval('V_ou_sum + N*V_ou', {
        N: space.quantity,
      }, 'V_ou_sum')
      ctx.endSection()
    });

    let res = ctx.V_ou_sum;
    ctx.endSection()
    return res
  }

  calc_V_ot_multi_zone_CAV() {
    let ctx = this.ctx
    ctx.startSection("V_ot multi zone CAV")

    ctx.P_system = this.system.estimatedTotalOccupancy.value
    ctx.P_sum_spaces = 0
    this.system.forEachSpace((space) => {
      ctx.P_sum_spaces += space.quantity * space.spaceType.getNumOccupants();
    })
    ctx.D = ctx.eval('min(1, P_system/max(1, P_sum_spaces))', {
    }, 'D')

    ctx.V_ou = this.calc_V_ou_multi_zone(RegularOrMin.Regular, ctx.D)
    ctx.V_ou_min = this.calc_V_ou_multi_zone(RegularOrMin.Min, ctx.D)

    // VentilationEfficiency_System
    ctx.E_v = ctx.eval('min(0.75, 0.88*D + 0.22)', {}, 'E_v')
    ctx.V_ot = ctx.eval('V_ou / E_v', {
    }, 'V_ot')
    ctx.V_ot_min = ctx.eval('V_ou_min / E_v', {
    }, 'V_ot_min')

    let res = {
      V_ot: ctx.V_ot,
      V_ot_min: ctx.V_ot_min,
      D: ctx.D,
      E_v: ctx.E_v,
      V_ou: ctx.V_ou,
      V_ou_min: ctx.V_ou_min,
    }
    ctx.endSection()
    return res
  }

  calc_V_ot_single_zone() {
    let ctx = this.ctx
    ctx.startSection("V_ot single zone")
    ctx.log("Single zone system: V_ot = V_oz (no multi-zone correction needed)")

    let zone = this.system.zones[0]
    let zoneSpace = zone.spaces[0]
    let spaceType = zoneSpace.getSpaceType()
    let ventValues = ctx.spaceVentilations.getValuesForSpaceType(spaceType)

    ctx.V_ot_cooling = ctx.eval('V_oz_cooling', {
      V_oz_cooling: ventValues.V_oz_cooling,
    }, 'V_ot_cooling')
    ctx.V_ot_cooling_min = ctx.eval('V_oz_cooling_min', {
      V_oz_cooling_min: ventValues.V_oz_cooling_min,
    }, 'V_ot_cooling_min')
    ctx.V_ot_heating = ctx.eval('V_oz_heating', {
      V_oz_heating: ventValues.V_oz_heating,
    }, 'V_ot_heating')
    ctx.V_ot_heating_min = ctx.eval('V_oz_heating_min', {
      V_oz_heating_min: ventValues.V_oz_heating_min,
    }, 'V_ot_heating_min')

    let res = {
      V_ot_heating: ctx.V_ot_heating,
      V_ot_heating_min: ctx.V_ot_heating_min,
      V_ot_cooling: ctx.V_ot_cooling,
      V_ot_cooling_min: ctx.V_ot_cooling_min,
      D: null,
      E_v: null,
      V_ou: null,
      V_ou_min: null,
    }
    ctx.endSection()
    return res
  }

  calc_V_ot() {
    let ctx = this.ctx
    ctx.startSection(`Calc V_ot (Outdoor air intake)`)
    if (this.system.usesAutoVentilation()) {
      if (this.system.hasSingleSpace()) {
        ctx.log("Single zone system: using single zone path (V_ot = V_oz)")
        ctx.V_ot_results = this.calc_V_ot_single_zone()
      } else {
        ctx.log("Multi-zone system: using multi-zone CAV calculations")
        let multizone = this.calc_V_ot_multi_zone_CAV()
        ctx.V_ot_results = {
          V_ot_heating: multizone.V_ot,
          V_ot_heating_min: multizone.V_ot_min,
          V_ot_cooling: multizone.V_ot,
          V_ot_cooling_min: multizone.V_ot_min,
          D: multizone.D,
          E_v: multizone.E_v,
          V_ou: multizone.V_ou,
          V_ou_min: multizone.V_ou_min,
        }
      }
    } else {
      ctx.log("Using manual system ventilation intake")
      ctx.V_ot = this.system.get_V_ot_manual();
      ctx.V_ot_results = {
        V_ot_heating: ctx.V_ot,
        V_ot_heating_min: null,
        V_ot_cooling: ctx.V_ot,
        V_ot_cooling_min: null,
        D: null,
        E_v: null,
        V_ou: null,
        V_ou_min: null,
      }
    }
    let res = ctx.V_ot_results
    ctx.endSection()
    return res
  }

  calc_V_ot_values() {
    let ctx = this.ctx
    ctx.startSection("Calc V_ot values")

    // Calculate V_ot for heating and cooling separately.
    // For multi-zone systems these will be equal; for single-zone they may differ (E_z differs).
    let V_ot_values = this.calc_V_ot()
    ctx.V_ot_heating = V_ot_values.V_ot_heating
    ctx.V_ot_heating_min = V_ot_values.V_ot_heating_min
    ctx.V_ot_cooling = V_ot_values.V_ot_cooling
    ctx.V_ot_cooling_min = V_ot_values.V_ot_cooling_min

    // If Demand-controlled ventilation is on, apply it here.
    // V_ot_min for DCV always comes from the manual dcvMinVentilation field.
    if (this.system.usingDemandControlledVentilation()) {
      ctx.log("Using demand-controlled ventilation. V_ot values depend on time.")
      ctx.V_ot_cooling_min = this.system.get_V_ot_min_for_dcv();
      ctx.V_ot_heating_min = this.system.get_V_ot_min_for_dcv();
      ctx.logValue('V_ot_cooling_min (DCV manual)', ctx.V_ot_cooling_min)
      // If the user-entered minimum exceeds the calculated V_ot, the minimum becomes the new V_ot.
      // Without this, the DCV interpolation formula would produce values below V_ot_min at full occupancy.
      if (ctx.V_ot_cooling_min > ctx.V_ot_cooling) {
        ctx.V_ot_cooling = ctx.V_ot_cooling_min;
        ctx.logValue('V_ot_cooling raised to V_ot_cooling_min', ctx.V_ot_cooling)
      }
      if (ctx.V_ot_heating_min > ctx.V_ot_heating) {
        ctx.V_ot_heating = ctx.V_ot_heating_min;
        ctx.logValue('V_ot_heating raised to V_ot_heating_min', ctx.V_ot_heating)
      }
      ctx.startLocalSection("Demand-controlled ventilation")
      ctx.V_ot_cooling_by_hr = makeVector(24)
      ctx.schedule = this.system.getDemandControlledVentilationSchedule(
        ctx.systemOccupancySchedule)
      for (let hr = 0; hr < 24; ++hr) {
        ctx.startLocalSection(`Hour ${hr}`)
        let V_ot_hr = ctx.eval('V_ot_cooling_min + S*(V_ot_cooling - V_ot_cooling_min)', {
          S: ctx.schedule[hr],
        }, 'V_ot_hr')
        ctx.V_ot_cooling_by_hr[hr] = V_ot_hr
        ctx.endSection()
      }
      ctx.logValue('V_ot_cooling_by_hr', ctx.V_ot_cooling_by_hr)
      ctx.endSection()
    } else {
      ctx.log("Not using demand-controlled ventilation. V_ot is not time-dependent.")
      ctx.V_ot_cooling_by_hr = makeVector(24).fill(ctx.V_ot_cooling)
    }

    let res = new OutdoorAirIntakeValues({
      V_ot_heating: ctx.V_ot_heating,
      V_ot_cooling_by_hr: ctx.V_ot_cooling_by_hr,
      D_cooling: V_ot_values.D,
      E_v_cooling: V_ot_values.E_v,
      V_ou_cooling: V_ot_values.V_ou,
    })
    ctx.endSection()
    return res
  }

  getSpaceLoadResults(zoneIndex, spaceIndex) {
    return this.zoneResults.getZoneResult(zoneIndex).getSpaceLoadResults(spaceIndex)
  }

  getSpaceType(zoneIndex, spaceIndex) {
    return this.system.zones[zoneIndex].spaces[spaceIndex].getSpaceType()
  }

  calcSpaceVentilations() {
    let ctx = this.ctx
    ctx.startSection("Calc all space ventilations")
    let spaceVentilations = new SpaceVentilationValues()
    this.system.forEachSpace((space) => {
      ctx.startLocalSection(`Zone ${space.zoneIndex}, Space ${space.spaceIndex}`)
      if (spaceVentilations.hasValuesForSpaceType(space.spaceType)) {
        ctx.log("Already have values for this space type")
        ctx.endSection();
        return;
      }
      let isSingleSpaceSystem = this.system.hasSingleSpace();
      ctx.ventilations = space.spaceType.ventilation.calc_Ventilation_values(ctx, isSingleSpaceSystem)
      spaceVentilations.setValuesForSpaceType(space.spaceType, ctx.ventilations)
      ctx.endSection()
    })
    let res = spaceVentilations
    ctx.endSection()
    return res
  }

  calc_Q_supply_cool_space(zoneIndex, spaceIndex) {
    let ctx = this.ctx
    ctx.startSection(`Q_supply_cool_space - Zone ${zoneIndex} Space ${spaceIndex}`)
    
    let space = this.getSpaceType(zoneIndex, spaceIndex)
    
    // Check for space supply flow override
    if (space.advancedOptions.overridesSpaceSupplyFlow()) {
      ctx.log("Using space supply flow override")
      let overrideValue = space.advancedOptions.getSupplyFlowRateOverride(ctx)
      ctx.Q_supply_cool_space = MatrixUtils.makeMatrixOfValue(12, 24, overrideValue)
      ctx.logLoadMatrix('Q_supply_cool_space (override)', ctx.Q_supply_cool_space)
      let res = ctx.Q_supply_cool_space
      ctx.endSection()
      return res
    }

    let spaceResults = this.getSpaceLoadResults(zoneIndex, spaceIndex)
    let q_cool_space_matrix = spaceResults.cooling.q_sensible
    ctx.v_supply_cool = ctx.call(psy.CalcPsychrometrics, ctx.T_supply_cool, ctx.altitude,
      PsyCalcMethod.CalcWithRelativeHumidity, {
        RH: 0.90
      }).v
    ctx.V_pz_min_space = ctx.spaceVentilations.getValuesForSpaceType(space).V_pz_min_cooling

    ctx.Q_supply_cool_space = math.zeros(12, 24)
    for (let mo = 0; mo < 12; ++mo) {
      ctx.startLocalSection(`Month ${mo}`)
      for (let hr = 0; hr < 24; ++hr) {
        ctx.startLocalSection(`Hour ${hr}`)
        ctx.T_cool_zone = this.get_T_zone_cooling(zoneIndex, hr)
        ctx.assert(ctx.T_supply_cool < ctx.T_cool_zone, "T_supply_cool must be less than T_cool_zone in order to " + 
          "cool the Space to the desired temperature.")
        ctx.Q_supply = ctx.eval('max(q_cool_space*v_supply_cool/(60*0.24*(T_cool_zone - T_supply_cool)), ' +
          'V_pz_min_space)', {
            q_cool_space: q_cool_space_matrix.get([mo, hr]),
          }, 'Q_supply')
        ctx.Q_supply_cool_space.set([mo, hr], ctx.Q_supply)
        ctx.endSection()
      }
      ctx.endSection()
    }

    ctx.logLoadMatrix('Q_supply_cool_space', ctx.Q_supply_cool_space)
    let res = ctx.Q_supply_cool_space
    ctx.endSection()
    return res
  }

  calc_Q_supply_heat_space(zoneIndex, spaceIndex) {
    let ctx = this.ctx
    ctx.startSection(`Q_supply_heat_space - Zone ${zoneIndex} Space ${spaceIndex}`)

    let space = this.getSpaceType(zoneIndex, spaceIndex)
    
    // Check for space supply flow override
    if (space.advancedOptions.overridesSpaceSupplyFlow()) {
      ctx.log("Using space supply flow override")
      let overrideValue = space.advancedOptions.getSupplyFlowRateOverride(ctx)
      ctx.Q_supply_heat_space = overrideValue
      ctx.logValue('Q_supply_heat_space (override)', ctx.Q_supply_heat_space)
      let res = ctx.Q_supply_heat_space
      ctx.endSection()
      return res
    }

    let spaceResults = this.getSpaceLoadResults(zoneIndex, spaceIndex)
    ctx.q_heat_space = spaceResults.heating.q_sensible;
    ctx.v_supply_heat = ctx.call(psy.CalcPsychrometrics, ctx.T_supply_heat, ctx.altitude,
      PsyCalcMethod.CalcWithRelativeHumidity, {
        RH: 0.20,
      }).v
    ctx.T_heat_zone = this.get_T_zone_heating(zoneIndex)
    ctx.V_pz_min_space = ctx.spaceVentilations.getValuesForSpaceType(space).V_pz_min_heating
    ctx.assert(ctx.T_supply_heat > ctx.T_heat_zone, "T_supply_heat must be greater than T_heat_zone in order to " +
      "heat the Space to the desired temperature.")
    ctx.Q_supply_heat_space = ctx.eval('max(q_heat_space*v_supply_heat/(60*0.24*(T_supply_heat - T_heat_zone)), ' +
      'V_pz_min_space)', {
      }, 'Q_supply_heat_space')
    
    let res = ctx.Q_supply_heat_space
    ctx.endSection()
    return res
  }

  get_Q_supply_cool_spaces(zoneIndex) {
    let ctx = this.ctx
    ctx.startSection("Q_supply_cool_spaces")
    let data = []
    this.system.forEachSpaceInZone(zoneIndex, (space) => {
      let Q_supply_cool_space = this.calc_Q_supply_cool_space(space.zoneIndex, space.spaceIndex)
      data.push(Q_supply_cool_space)
    })
    ctx.endSection()
    return data
  }

  get_Q_supply_heat_spaces(zoneIndex) {
    let ctx = this.ctx
    ctx.startSection("Q_supply_heat_spaces")
    let data = []
    this.system.forEachSpaceInZone(zoneIndex, (space) => {
      let Q_supply_heat_space = this.calc_Q_supply_heat_space(space.zoneIndex, space.spaceIndex)
      data.push(Q_supply_heat_space)
    })
    ctx.endSection()
    return data
  }

  calc_Q_supply_CAV() {
    /*
    Note: For CAV, Q_supply_cooling is a matrix (of all the same value) and Q_supply_heating is a scalear
    */
    let ctx = this.ctx;
    ctx.startSection("Calc Q_supply CAV")
    ctx.separateAirflows = this.system.separateAirflow.value;
    ctx.zoneAirflowCalcMethod = this.system.cavSystemInputs.zoneAirflowCalcMethod.value;

    ctx.Q_zone_supply_cooling_values = []
    ctx.Q_zone_supply_heating_values = []

    ctx.Q_space_supply_cooling_values = []
    ctx.Q_space_supply_heating_values = []

    this.system.forEachZone((zone) => {
      ctx.startLocalSection("Zone " + zone.zoneIndex)

      let Q_supply_cooling_arr = this.get_Q_supply_cool_spaces(zone.zoneIndex)
      let Q_supply_heating_arr = this.get_Q_supply_heat_spaces(zone.zoneIndex)
      ctx.spaceQuantities = zone.zone.getSpaceQuantities()

      ctx.Q_space_supply_cooling_values.push(Q_supply_cooling_arr)
      ctx.Q_space_supply_heating_values.push(Q_supply_heating_arr)
      
      if (ctx.zoneAirflowCalcMethod == ZoneAirflowCalcMethod.SumOfPeaks) {
        let Q_zone_supply_cooling = ctx.eval('sumOfMatrixMaxs(applySpaceQuantities(Q_supply_cooling_arr, spaceQuantities))', {
          sumOfMatrixMaxs: MatrixUtils.sumOfMatrixMaxs,
          applySpaceQuantities,
          Q_supply_cooling_arr,
        }, 'Q_zone_supply_cooling')

        let Q_zone_supply_heating = ctx.eval('sum(applySpaceQuantities(Q_supply_heating_arr, spaceQuantities))', {
          sum: scalarSum,
          applySpaceQuantities,
          Q_supply_heating_arr,
        }, 'Q_zone_supply_heating')

        if (ctx.separateAirflows == YesNo.Yes) {
          ctx.log("Separate airflows")
          ctx.Q_zone_supply_cooling = Q_zone_supply_cooling
          ctx.Q_zone_supply_heating = Q_zone_supply_heating
        } else {
          ctx.log("Combined airflows, taking max")
          let max_Q_zone_supply = Math.max(Q_zone_supply_cooling, Q_zone_supply_heating)
          ctx.Q_zone_supply_cooling = max_Q_zone_supply
          ctx.Q_zone_supply_heating = max_Q_zone_supply
        }
      } else if (ctx.zoneAirflowCalcMethod == ZoneAirflowCalcMethod.TotalPeak) {
        let Q_zone_supply_cooling = ctx.eval('maxInMatrix(matrixSum(applySpaceQuantities(Q_supply_cooling_arr, spaceQuantities)))', {
          maxInMatrix: MatrixUtils.maxInMatrix,
          matrixSum: MatrixUtils.sumOfMatrices,
          applySpaceQuantities,
          Q_supply_cooling_arr,
        }, 'Q_zone_supply_cooling')

        let Q_zone_supply_heating = ctx.eval('sum(applySpaceQuantities(Q_supply_heating_arr, spaceQuantities))', {
          sum: scalarSum,
          Q_supply_heating_arr,
          applySpaceQuantities,
        }, 'Q_zone_supply_heating')

        if (ctx.separateAirflows == YesNo.Yes) {
          ctx.log("Separate airflows")
          ctx.Q_zone_supply_cooling = Q_zone_supply_cooling
          ctx.Q_zone_supply_heating = Q_zone_supply_heating
        } else {
          ctx.log("Combined airflows, taking max")
          let max_Q_zone_supply = Math.max(Q_zone_supply_cooling, Q_zone_supply_heating)
          ctx.Q_zone_supply_cooling = max_Q_zone_supply
          ctx.Q_zone_supply_heating = max_Q_zone_supply
        }
      } else {
        throw new Error("Unsupported zoneAirflowCalcMethod: " + ctx.zoneAirflowCalcMethod)
      }

      ctx.log("Making Q_supply_cooling matrix from value")
      ctx.Q_zone_supply_cooling = ctx.eval('makeMatrixOfValue(12, 24, Q_zone_supply_cooling)', {
        makeMatrixOfValue: MatrixUtils.makeMatrixOfValue,
      }, 'Q_supply_cooling')

      ctx.Q_zone_supply_cooling_values.push(ctx.Q_zone_supply_cooling)
      ctx.Q_zone_supply_heating_values.push(ctx.Q_zone_supply_heating)
      ctx.endSection()
    })

    ctx.Q_supply_cooling = ctx.eval('matrixSum(Q_zone_supply_cooling_values)', {
      matrixSum: MatrixUtils.sumOfMatrices,
      Q_zone_supply_cooling_values: ctx.Q_zone_supply_cooling_values,
    }, 'Q_supply_cooling')
    ctx.Q_supply_heating = ctx.eval('sum(Q_zone_supply_heating_values)', {
      sum: scalarSum,
      Q_zone_supply_heating_values: ctx.Q_zone_supply_heating_values,
    }, 'Q_supply_heating')

    // Clamp to V_ot_values
    ctx.log("Clamping to V_ot values if necessary")
    ctx.V_ot_cooling_matrix = ctx.V_ot_values.get_V_ot_cooling_matrix()
    ctx.Q_supply_cooling = ctx.eval('matrixClampToMin(Q_supply_cooling, V_ot_cooling_matrix)', {
      matrixClampToMin: MatrixUtils.matrixClampToMin,
    }, 'Q_supply_cooling')
    ctx.V_ot_heating = ctx.V_ot_values.get_V_ot_heating()
    ctx.Q_supply_heating = ctx.eval('clampToMin(Q_supply_heating, V_ot_heating)', {
      clampToMin,
    }, 'Q_supply_heating')

    ctx.logLoadMatrix('Q_supply_cooling', ctx.Q_supply_cooling)
    ctx.logValue('Q_supply_heating', ctx.Q_supply_heating)

    let res = {
      Q_space_supply_cooling_values: ctx.Q_space_supply_cooling_values,
      Q_space_supply_heating_values: ctx.Q_space_supply_heating_values,
      Q_zone_supply_cooling_values: ctx.Q_zone_supply_cooling_values,
      Q_zone_supply_heating_values: ctx.Q_zone_supply_heating_values,
      Q_supply_cooling: ctx.Q_supply_cooling,
      Q_supply_heating: ctx.Q_supply_heating,
    }
    ctx.endSection()
    return res
  }

  calc_Q_supply_VAV() {
    /*
    Note: For VAV, Q_supply_cooling is a matrix, and Q_supply_heating is a scalar
    */
    let ctx = this.ctx
    ctx.startSection("Calc Q_supply VAV")

    ctx.Q_zone_supply_cooling_values = []
    ctx.Q_zone_supply_heating_values = []

    ctx.Q_space_supply_cooling_values = []
    ctx.Q_space_supply_heating_values = []

    this.system.forEachZone((zone) => {
      ctx.startLocalSection("Zone " + zone.zoneIndex)

      let Q_supply_cooling_arr = this.get_Q_supply_cool_spaces(zone.zoneIndex)
      let Q_supply_heating_arr = this.get_Q_supply_heat_spaces(zone.zoneIndex)
      ctx.spaceQuantities = zone.zone.getSpaceQuantities()

      ctx.Q_space_supply_cooling_values.push(Q_supply_cooling_arr)
      ctx.Q_space_supply_heating_values.push(Q_supply_heating_arr)

      ctx.Q_zone_supply_cooling = ctx.eval('matrixSum(applySpaceQuantities(Q_supply_cooling_arr, spaceQuantities))', {
        matrixSum: MatrixUtils.sumOfMatrices,
        applySpaceQuantities,
        Q_supply_cooling_arr,
      }, 'Q_zone_supply_cooling')

      ctx.Q_zone_supply_heating = ctx.eval('sum(applySpaceQuantities(Q_supply_heating_arr, spaceQuantities))', {
        sum: scalarSum,
        applySpaceQuantities,
        Q_supply_heating_arr,
      }, 'Q_zone_supply_heating')

      ctx.Q_zone_supply_cooling_values.push(ctx.Q_zone_supply_cooling)
      ctx.Q_zone_supply_heating_values.push(ctx.Q_zone_supply_heating)
      ctx.endSection()
    })

    ctx.Q_supply_cooling = ctx.eval('matrixSum(Q_zone_supply_cooling_values)', {
      matrixSum: MatrixUtils.sumOfMatrices,
      Q_zone_supply_cooling_values: ctx.Q_zone_supply_cooling_values,
    }, 'Q_supply_cooling')
    ctx.Q_supply_heating = ctx.eval('sum(Q_zone_supply_heating_values)', {
      sum: scalarSum,
      Q_zone_supply_heating_values: ctx.Q_zone_supply_heating_values,
    }, 'Q_supply_heating')


    // Clamp to V_ot_values
    ctx.log("Clamping to V_ot values if necessary")
    ctx.V_ot_cooling_matrix = ctx.V_ot_values.get_V_ot_cooling_matrix()
    ctx.Q_supply_cooling = ctx.eval('matrixClampToMin(Q_supply_cooling, V_ot_cooling_matrix)', {
      matrixClampToMin: MatrixUtils.matrixClampToMin,
    }, 'Q_supply_cooling')
    ctx.V_ot_heating = ctx.V_ot_values.get_V_ot_heating()
    ctx.Q_supply_heating = ctx.eval('clampToMin(Q_supply_heating, V_ot_heating)', {
      clampToMin,
    }, 'Q_supply_heating')

    ctx.logLoadMatrix('Q_supply_cooling', ctx.Q_supply_cooling)
    ctx.logValue('Q_supply_heating', ctx.Q_supply_heating)

    let res = {
      Q_space_supply_cooling_values: ctx.Q_space_supply_cooling_values,
      Q_space_supply_heating_values: ctx.Q_space_supply_heating_values,
      Q_zone_supply_cooling_values: ctx.Q_zone_supply_cooling_values,
      Q_zone_supply_heating_values: ctx.Q_zone_supply_heating_values,
      Q_supply_cooling: ctx.Q_supply_cooling,
      Q_supply_heating: ctx.Q_supply_heating,
    }
    ctx.endSection()
    return res
  }

  normalize_Q_supply_values(Q_supply_values, V_ot_values) {
    // TODO - this is not yet figured out
    let ctx = this.ctx
    ctx.startSection("Normalize Q_supply values")
    // If V_ot exceeds Q_supply, we increase each Q_supply accordingly:
    //	If V_ot > Q_supply_cool:
		//      For each space (j):
		//             Q_supply_cool_space_j = Q_supply_cool_space_j * (V_ot / Q_supply_cool)
    // 
    // (And same for heating)
    // TODO - implement this, according to the logic above. Must apply the same 
    ctx.endSection()
  }

  calc_Q_supply() {
    let ctx = this.ctx
    ctx.startSection("Calc Q_supply")

    ctx.systemType = this.system.getSystemType()
    if (ctx.systemType == SystemType.CAV) {
      ctx.res = this.calc_Q_supply_CAV()
    } else if (ctx.systemType == SystemType.VAV) {
      ctx.res = this.calc_Q_supply_VAV()
    } else {
      throw new Error("Unsupported system type: " + ctx.systemType)
    }

    let res = new QSupplyValues(ctx.res)
    // TODO - this is not yet figured out
    //ctx.require('V_ot_values')
    //this.normalize_Q_supply_values(res, ctx.V_ot_values)

    ctx.endSection()
    return res
  }

  // System occupancy
  calc_O_system(hr) {
    let ctx = this.ctx
    ctx.startSection("Calc O_system")
    ctx.O_system = 0
    this.system.forEachSpace((space) => {
      ctx.O_space = space.spaceType.getNumOccupants()
      ctx.S_space = space.spaceType.getOccupancySchedule().getData()[hr]
      ctx.O_space = ctx.eval('N*O_space*S_space', {
        N: space.quantity,
      }, 'O_space')
      ctx.O_system += ctx.O_space
    })
    let res = ctx.O_system
    ctx.endSection()
    return res
  }

  get_T_zone_cooling(zoneIndex, hr) {
    // NOTE - we used to have a check for systemOccupied here, but now just use a fixed value
    let zone = this.system.zones[zoneIndex]
    return zone.summerIndoorTemp.value
  }

  get_T_zone_heating(zoneIndex) {
    let zone = this.system.zones[zoneIndex]
    return zone.winterIndoorTemp.value
  }

  calc_v_zone_cooling(zoneIndex, hr) {
    let ctx = this.ctx
    ctx.startSection(`Calc v_zone cooling - Zone ${zoneIndex}, Hr ${hr}`)
    let zone = this.system.zones[zoneIndex]
    ctx.T_zone = this.get_T_zone_cooling(zoneIndex, hr)
    ctx.Humidity_setpoint_zone = zone.humiditySetpoint.value / 100.0
    ctx.v_zone = ctx.call(psy.CalcPsychrometrics, ctx.T_zone, ctx.altitude,
      PsyCalcMethod.CalcWithRelativeHumidity, {
        RH: ctx.Humidity_setpoint_zone,
      }).v
    let res = ctx.v_zone
    ctx.endSection()
    return res
  }

  calc_v_zone_heating(zoneIndex) {
    let ctx = this.ctx
    ctx.startSection(`Calc v_zone heating - Zone ${zoneIndex}`)
    ctx.T_zone = this.get_T_zone_heating(zoneIndex)
    ctx.v_zone = ctx.call(psy.CalcPsychrometrics, ctx.T_zone, ctx.altitude,
      PsyCalcMethod.CalcWithRelativeHumidity, {
        RH: 0.30,
      }).v
    let res = ctx.v_zone
    ctx.endSection()
    return res
  }

  calc_V_zone(zoneIndex) {
    /**
     * Calc the total volume of the zone
     */
    let ctx = this.ctx
    ctx.startSection(`Calc V_zone - Zone ${zoneIndex}`)
    ctx.V_zone = 0
    this.system.forEachSpaceInZone(zoneIndex, (space) => {
      ctx.startLocalSection(`Space ${space.spaceIndex}`)
      ctx.V_space = ctx.eval('N*floorArea*height', {
        N: space.quantity,
        floorArea: space.spaceType.floorArea.value,
        height: space.spaceType.avgCeilingHeight.value,
      }, 'V_space')
      ctx.V_zone += ctx.V_space * space.quantity
      ctx.endSection()
    })
    ctx.V_zone = ctx.eval('max(V_zone, 1)', {}, 'V_zone')
    let res = ctx.V_zone
    ctx.endSection()
    return res
  }

  calc_W_return_cooling(mo, hr) {
    let ctx = this.ctx
    ctx.startSection("Calc W_return")
    ctx.require('m_return_zones')
    ctx.require('m_return')
    ctx.require('T_return')
    ctx.require('v_supply')
    ctx.Q_return = ctx.Q_supply_values.getSystemSupplyCooling(mo, hr)

    ctx.W_return_sum = 0
    this.system.forEachZone((zone) => {
      ctx.startLocalSection("Zone " + zone.zoneIndex)
      ctx.T_zone = this.get_T_zone_cooling(zone.zoneIndex, hr)
      ctx.Humidity_setpoint_zone = zone.zone.humiditySetpoint.value / 100.0
      // This is the old way of calculating W_zone
      /*
      ctx.W_zone = ctx.call(psy.CalcPsychrometrics, ctx.T_zone, ctx.altitude,
        PsyCalcMethod.CalcWithRelativeHumidity, {
          RH: ctx.Humidity_setpoint_zone,
        }).W
      */

      // Use the new equation for W_zone
      let zoneResults = this.zoneResults.getZoneResult(zone.zoneIndex)
      ctx.W_supply_max = ctx.call(psy.CalcPsychrometrics, ctx.T_supply_cool, ctx.altitude,
        PsyCalcMethod.CalcWithRelativeHumidity, {
          RH: 0.95,
        }).W
      ctx.q_lat_zone = zoneResults.getZoneTotals().cooling.q_latent.get([mo, hr])
      ctx.Q_supply_zone = ctx.Q_supply_values.getZoneSupplyCooling(zone.zoneIndex, mo, hr)
      ctx.W_zone_sat = ctx.call(psy.CalcPsychrometrics, ctx.T_zone, ctx.altitude,
        PsyCalcMethod.CalcWithRelativeHumidity, {
          RH: 0.95,
        }).W
      ctx.T_db_avg = ctx.eval('(T_supply_cool + T_zone) / 2', {
      }, 'T_db_avg')
      ctx.W_zone = ctx.eval('min(W_supply_max + safeDivide(q_lat_zone, 60*Q_supply_zone*1.0/v_supply*(1061 + 0.444*T_db_avg)), W_zone_sat)', {
        max: Math.max,
        safeDivide,
      }, 'W_zone')
      // Note: the W_zone may be negative is some cases when q_lat_zone is negative, so we clamp here
      // to avoid an error.
      ctx.W_zone = ctx.eval('max(W_zone, 0.00005)', {}, 'W_zone')
      ctx.m_return_zone = ctx.m_return_zones[zone.zoneIndex]
      ctx.W_return_item = ctx.eval('W_zone*m_return_zone', {
      }, 'W_return_item')

      ctx.W_return_sum += ctx.W_return_item
      ctx.endSection()
    })
    ctx.W_return_sum /= ctx.m_return

    ctx.W_return_max = ctx.call(psy.CalcPsychrometrics, ctx.T_return, ctx.altitude,
      PsyCalcMethod.CalcWithRelativeHumidity, {
        RH: 1.0,
      }).W
    ctx.W_return = ctx.eval('min(W_return_sum, W_return_max)', {
    }, 'W_return');

    let res = ctx.W_return
    ctx.endSection()
    return res
  }

  calc_W_return_heating() {
    let ctx = this.ctx
    ctx.startSection("Calc W_return")
    ctx.require('m_return_zones')
    ctx.require('m_return')

    ctx.W_return = 0
    this.system.forEachZone((zone) => {
      ctx.startLocalSection("Zone " + zone.zoneIndex)
      ctx.log("Note: We assume winter humidity is 0.30 in all zones")
      ctx.T_zone = this.get_T_zone_heating(zone.zoneIndex)
      ctx.W_zone = ctx.call(psy.CalcPsychrometrics, ctx.T_zone, ctx.altitude,
        PsyCalcMethod.CalcWithRelativeHumidity, {
          RH: 0.30,
        }).W
      ctx.m_return_zone = ctx.m_return_zones[zone.zoneIndex]
      ctx.W_return_item = ctx.eval('W_zone*m_return_zone', {
      }, 'W_return_item')
      ctx.W_return += ctx.W_return_item
      ctx.endSection()
    })
    ctx.W_return = ctx.eval('W_return / m_return', {}, 'W_return');
    let res = ctx.W_return
    ctx.endSection()
    return res
  }

  calc_q_plenum_matrix() {
    let ctx = this.ctx
    ctx.startSection("Calc q_plenum matrix")
    ctx.q_plenum = math.zeros(12, 24)
    this.system.forEachSpace((space) => {
      ctx.startLocalSection(`Zone ${space.zoneIndex}, Space ${space.spaceIndex}`)
      let results = this.getSpaceLoadResults(space.zoneIndex, space.spaceIndex)
      ctx.item = results.plenum_loads
      ctx.item = math.multiply(ctx.item, space.quantity)
      ctx.q_plenum = math.add(ctx.q_plenum, ctx.item)
      ctx.endSection()
    })
    let res = ctx.q_plenum
    ctx.logLoadMatrix('q_plenum', ctx.q_plenum)
    ctx.endSection()
    return res
  }

  calc_q_plenum(mo, hr) {
    return this.ctx.q_plenum_matrix.get([mo, hr])
  }

  calc_q_fan_supply() {
    let ctx = this.ctx
    ctx.startSection("Calc q_fan_supply")
    let systemFans = this.system.systemFans
    if (systemFans.useSupplyFan.value == YesNo.No) {
      ctx.log("No supply fan")
      ctx.endSection()
      return 0;
    }
    let supplyFan = systemFans.supplyFan
    ctx.require('m_recirc')
    ctx.require('m_vent')
    // Air power of supply fan
    ctx.P_A_supply = ctx.eval('0.00209*(m_recirc+m_vent)*p_supply', {
      p_supply: supplyFan.externalStaticPressure.value,
    }, 'P_A_supply')
    // Fan power of supply fan
    ctx.P_F_supply = ctx.eval('P_A_supply/n_F_supply', {
      n_F_supply: supplyFan.fanEfficiency.value / 100.0,
    }, 'P_F_supply')

    if (supplyFan.motorIsInAirStream.value == YesNo.Yes) {
      ctx.log("Motor is in air stream")
      ctx.P_M_supply = ctx.eval('P_F_supply / E_M_supply', {
        E_M_supply: supplyFan.motorEfficiency.value / 100.0,
      }, 'P_M_supply')
      ctx.q_fan_supply = ctx.eval('2545*P_M_supply', {}, 'q_fan_supply')
    } else {
      ctx.log("Motor is not in air stream")
      ctx.q_fan_supply = ctx.eval('2545*P_F_supply', {}, 'q_fan_supply')
    }
    let res = ctx.q_fan_supply
    ctx.endSection()
    return res
  }

  calc_q_fan_return(m_return) {
    let ctx = this.ctx
    ctx.startSection("Calc q_fan_return")
    let systemFans = this.system.systemFans
    if (systemFans.useReturnFan.value == YesNo.No) {
      ctx.log("No return fan")
      ctx.endSection()
      return 0;
    }
    let returnFan = systemFans.returnFan
    ctx.m_return = m_return
    ctx.P_A_return = ctx.eval('0.00209*m_return*p_return', {
      p_return: returnFan.externalStaticPressure.value,
    }, 'P_A_return')
    ctx.P_F_return = ctx.eval('P_A_return/n_F_return', {
      n_F_return: returnFan.fanEfficiency.value / 100.0,
    }, 'P_F_return')

    if (returnFan.motorIsInAirStream.value == YesNo.Yes) {
      ctx.log("Motor is in air stream")
      ctx.P_M_return = ctx.eval('P_F_return / E_M_return', {
        E_M_return: returnFan.motorEfficiency.value / 100.0,
      }, 'P_M_return')
      ctx.q_fan_return = ctx.eval('2545*P_M_return', {
      }, 'q_fan_return')
    } else {
      ctx.log("Motor is not in air stream")
      ctx.q_fan_return = ctx.eval('2545*P_F_return', {
      }, 'q_fan_return')
    }

    let res = ctx.q_fan_return
    ctx.endSection()
    return res
  }

  calc_W_supply_cooling(W_mix) {
    let ctx = this.ctx
    ctx.startSection("Calc W_supply")

    ctx.require('T_supply_cool')
    ctx.W_coil_sat = ctx.call(psy.CalcPsychrometrics, ctx.T_supply_cool, ctx.altitude,
      PsyCalcMethod.CalcWithRelativeHumidity, {
        RH: 0.95,
    }).W
    ctx.W_supply = ctx.eval('min(W_mix, W_coil_sat)', {
      W_mix: W_mix,
    }, 'W_supply')

    // For version 2:
    /*
    ctx.require('v_supply')
    ctx.require('peak_Q_supply')
    ctx.require('peak_Q_supply_mo')
    ctx.require('peak_Q_supply_hr')
    let peakMo = ctx.peak_Q_supply_mo
    let peakHr = ctx.peak_Q_supply_hr
    ctx.mixValuesAtPeak = this.calc_mix_values_cooling(peakMo, peakHr)
    ctx.T_mix_peak = ctx.mixValuesAtPeak.T_mix
    ctx.W_return_peak = ctx.mixValuesAtPeak.W_return
    // TODO - how to get this?
    ctx.q_lat_peak = 0
    ctx.T_db_avg_peak = ctx.eval('(T_supply_cool+T_mix_peak)/2.0', {
    }, 'T_db_avg_peak')
    ctx.W_supply = ctx.eval('W_return_peak - q_lat_peak/(60*peak_Q_supply*(1.0/v_supply))*' +
      '(1061+0.444*T_db_avg_peak)', {
      }, 'W_supply')
    */

    let res = ctx.W_supply
    ctx.endSection()
    return res
  }

  calc_m_return_zones_cooling(mo, hr) {
    let ctx = this.ctx
    ctx.startSection("Calc m_return cooling")

    let m_return_zones = []
    this.system.forEachZone((zone) => {
      ctx.startLocalSection(`Zone ${zone.zoneIndex}`)
      ctx.Q_supply_zone = ctx.Q_supply_values.getZoneSupplyCooling(zone.zoneIndex, mo, hr)
      ctx.v_zone = this.calc_v_zone_cooling(zone.zoneIndex, hr)
      ctx.m_return_zone = ctx.eval('Q_supply_zone/v_zone', {
      }, 'm_return_zone')
      m_return_zones.push(ctx.m_return_zone)
      ctx.endSection()
    })

    let res = m_return_zones
    ctx.endSection()
    return res
  }

  calc_m_return_zones_heating() {
    let ctx = this.ctx
    ctx.startSection("Calc m_return heating")

    let m_return_zones = []
    this.system.forEachZone((zone) => {
      ctx.startLocalSection(`Zone ${zone.zoneIndex}`)
      ctx.Q_supply_zone = ctx.Q_supply_values.getZoneSupplyHeating(zone.zoneIndex)
      ctx.v_zone = this.calc_v_zone_heating(zone.zoneIndex)
      ctx.m_return_zone = ctx.eval('Q_supply_zone/v_zone', {
      }, 'm_return_zone')
      m_return_zones.push(ctx.m_return_zone)
      ctx.endSection()
    })

    let res = m_return_zones
    ctx.endSection()
    return res
  }

  calc_return_values_cooling(mo, hr) {
    let ctx = this.ctx
    ctx.startSection("Calc return values cooling")

    ctx.m_return_zones = this.calc_m_return_zones_cooling(mo, hr)
    ctx.m_return = ctx.eval('sum(m_return_zones)', {
      sum: scalarSum,
    }, 'm_return')

    ctx.Q_supply = ctx.Q_supply_values.getSystemSupplyCooling(mo, hr)

    let innerSumElems = []
    this.system.forEachZone((zone) => {
      let T_zone = this.get_T_zone_cooling(zone.zoneIndex, hr)
      let Q_supply_zone = ctx.Q_supply_values.getZoneSupplyCooling(zone.zoneIndex, mo, hr)
      innerSumElems.push({
        T_zone: T_zone,
        Q_supply_zone: Q_supply_zone,
      })
    })
    ctx.innerSum = ctx.evalSum('T_zone*Q_supply_zone', innerSumElems, 'innerSum')  
    ctx.q_plenum = this.calc_q_plenum(mo, hr)
    ctx.q_fan_return = this.calc_q_fan_return(ctx.m_return)

    // T_zone_mix is always physically valid: a mass-flow-weighted average of zone setpoints.
    ctx.T_zone_mix = ctx.eval('(1.0/Q_supply)*innerSum', {}, 'T_zone_mix')

    let recoveryType = this.system.heatRecovery.recoveryType.value
    if (recoveryType === RecoveryType.None) {
      // For non-recovery systems: exclude q_plenum from T_return entirely.
      // Plenum heat is applied to the mixed-air enthalpy in calc_mix_values_cooling instead,
      // which prevents the blow-up that occurs when q_plenum is large and negative while
      // m_return is small (VAV at minimum flow). q_fan_return is proportional to m_return,
      // so q_fan_return/m_return is bounded and safe to keep here.
      if (Math.abs(ctx.m_return) >= M_RETURN_EPSILON) {
        ctx.T_return = ctx.eval('T_zone_mix + q_fan_return/(60*0.24*m_return)', {}, 'T_return')
      } else {
        ctx.log('m_return near zero; setting T_return = T_zone_mix')
        ctx.T_return = ctx.T_zone_mix
      }
    } else {
      // For HRV/ERV systems: include q_plenum in T_return so the heat-recovery device
      // sees the plenum-warmed exhaust air, matching the physical arrangement where exhaust
      // grilles draw from the return plenum before the recovery device.
      // Clamp to T_supply_cool as a physical lower bound: return air cannot realistically
      // drop below supply-air temperature when airflow is very low and plenum loads are
      // large and negative (the formula over-predicts cooling at near-zero mass flow).
      if (Math.abs(ctx.m_return) >= M_RETURN_EPSILON) {
        ctx.T_return = ctx.eval('T_zone_mix + (q_plenum+q_fan_return)/(60*0.24*m_return)', {}, 'T_return')
        if (ctx.T_return < ctx.T_supply_cool) {
          ctx.log(`T_return (${ctx.T_return.toFixed(1)}°F) below supply temperature; ` +
            `clamping to T_supply_cool (${ctx.T_supply_cool.toFixed(1)}°F)`)
          ctx.T_return = ctx.T_supply_cool
        }
      } else {
        ctx.log('m_return near zero; setting T_return = T_zone_mix')
        ctx.T_return = ctx.T_zone_mix
      }
    }
    ctx.W_return = this.calc_W_return_cooling(mo, hr)
    ctx.h_return = ctx.eval('0.24*T_return+W_return*(1061+0.444*T_return)', {
    }, 'h_return')
    let returnAirPsychrometrics = ctx.call(psy.CalcPsychrometrics, ctx.T_return, ctx.altitude,
      PsyCalcMethod.CalcWithHumidityRatio, {
        W: ctx.W_return,
      })
    ctx.v_return = returnAirPsychrometrics.v

    let res = {
      T_return: ctx.T_return,
      W_return: ctx.W_return,
      h_return: ctx.h_return,
      v_return: ctx.v_return,
      returnAirPsychrometrics: returnAirPsychrometrics,
    }
    ctx.endSection("Calc return values cooling")
    return res
  }

  calc_return_values_heating() {
    let ctx = this.ctx
    ctx.startSection("Calc return values heating")

    ctx.m_return_zones = this.calc_m_return_zones_heating()
    ctx.m_return = ctx.eval('sum(m_return_zones)', {
      sum: scalarSum,
    }, 'm_return')
    ctx.Q_supply = ctx.Q_supply_values.getSystemSupplyHeating()

    let innerSumElems = []
    this.system.forEachZone((zone) => {
      let T_zone = this.get_T_zone_heating(zone.zoneIndex)
      let Q_supply_zone = ctx.Q_supply_values.getZoneSupplyHeating(zone.zoneIndex)
      innerSumElems.push({
        T_zone: T_zone,
        Q_supply_zone: Q_supply_zone,
      })
    })
    ctx.innerSum = ctx.evalSum('T_zone*Q_supply_zone', innerSumElems, 'innerSum')  
    ctx.q_fan_return = this.calc_q_fan_return(ctx.m_return)
    ctx.T_return = ctx.eval('(1.0/Q_supply)*innerSum + q_fan_return/'+
      '(60*0.24*m_return)', {
    }, 'T_return')
    ctx.W_return = this.calc_W_return_heating()
    ctx.h_return = ctx.eval('0.24*T_return+W_return*(1061+0.444*T_return)', {
    }, 'h_return')
    ctx.v_return = ctx.call(psy.CalcPsychrometrics, ctx.T_return, ctx.altitude,
      PsyCalcMethod.CalcWithHumidityRatio, {
        W: ctx.W_return,
      }).v

    let res = {
      T_return: ctx.T_return,
      W_return: ctx.W_return,
      h_return: ctx.h_return,
      v_return: ctx.v_return,
    }

    ctx.endSection()
    return res
  }

  calc_mix_values_cooling(mo, hr) {
    let ctx = this.ctx
    ctx.startSection("Calc mix values cooling")

    let heatRecovery = this.system.heatRecovery;
    ctx.recoveryType = heatRecovery.recoveryType.value

    let t_db_out = this.calc_T_db_out_cooling(hr, mo)
    let t_wb_out = this.calc_T_wb_out_cooling(hr, mo)
    let outdoorAirPsychrometrics = ctx.call(psy.CalcPsychrometrics, t_db_out, ctx.altitude,
      PsyCalcMethod.CalcWithWetBulbTemp, {
        t_wb_F: t_wb_out
      })
    ctx.h_oa = outdoorAirPsychrometrics.h
    ctx.W_oa = outdoorAirPsychrometrics.W
    ctx.T_oa = t_db_out

    let returnValues = this.calc_return_values_cooling(mo, hr)
    ctx.T_return = returnValues.T_return
    ctx.W_return = returnValues.W_return
    ctx.h_return = returnValues.h_return
    ctx.v_return = returnValues.v_return

    ctx.V_ot_hr = ctx.V_ot_values.get_V_ot_cooling_for_hr(hr)
    ctx.m_vent = ctx.eval('V_ot_hr*air_density', {
    }, 'm_vent')
    ctx.Q_return = ctx.Q_supply_values.getSystemSupplyCooling(mo, hr)
    ctx.log(`TODO - modified this here to set m_recirc = max(0, ...)`)
    ctx.m_recirc = ctx.eval('max(Q_return-V_ot_hr, 0)/v_return', {
      max: Math.max,
    }, 'm_recirc')
    if (ctx.recoveryType != RecoveryType.None) {
      // These vars are only needed if RecoveryType is not None
      ctx.Q_exhaust = heatRecovery.calc_Q_exhaust(ctx, ctx.V_ot_hr)
      ctx.m_exhaust = ctx.eval('Q_exhaust/v_return', {
      }, 'm_exhaust')
      ctx.m_min = ctx.eval('min(m_exhaust, m_vent)', {
      }, 'm_min')
    }

    if (ctx.recoveryType == RecoveryType.None) {
      ctx.log(`Calc h_vent, T_vent, W_vent for recovery type None`)
      ctx.h_vent = ctx.h_oa
      ctx.T_vent = ctx.T_oa
      ctx.W_vent = ctx.W_oa
    } else if (ctx.recoveryType == RecoveryType.HRV) {
      ctx.log(`Calc h_vent, T_vent, W_vent for recovery type HRV`)
      let hrvInputs = heatRecovery.hrvGroup
      ctx.T_vent = ctx.eval('T_oa-(epsilon_s*m_min/max(m_vent, 0.0001) * (T_oa-T_return))', {
        epsilon_s: hrvInputs.get('summerEfficiency').value / 100.0,
      }, 'T_vent')
      ctx.W_sat_vent = ctx.call(psy.CalcPsychrometrics, ctx.T_vent, ctx.altitude,
        PsyCalcMethod.CalcWithRelativeHumidity, {
        RH: 1.0,
      }).W
      ctx.W_vent = ctx.eval('min(W_oa, W_sat_vent)', {
      }, 'W_vent')
      ctx.h_vent = ctx.eval('0.24*T_vent+W_vent*(1061+0.444*T_vent)', {
      }, 'h_vent')
    } else if (ctx.recoveryType == RecoveryType.ERV) {
      ctx.log(`Calc h_vent, T_vent, W_vent for recovery type ERV`)
      let ervInputs = heatRecovery.ervGroup
      ctx.h_vent = ctx.eval('h_oa-(epsilon_T*m_min/max(m_vent, 0.0001)*(h_oa-h_return))', {
        epsilon_T: ervInputs.get('summerTotalEfficiency').value / 100.0,
      }, 'h_vent')
      ctx.T_vent = ctx.eval('T_oa-(epsilon_s*m_min/max(m_vent, 0.0001)*(T_oa-T_return))', {
        epsilon_s: ervInputs.get('summerSensibleEfficiency').value / 100.0,
      }, 'T_vent')
      ctx.W_sat_vent = ctx.call(psy.CalcPsychrometrics, ctx.T_vent, ctx.altitude,
        PsyCalcMethod.CalcWithRelativeHumidity, {
        RH: 1.0,
      }).W
      ctx.W_vent = ctx.eval('min((h_vent-0.24*T_vent)/(1061+0.444*T_vent), W_sat_vent)', {
      }, 'W_vent')
    } else {
      throw new Error("Unsupported recovery type: " + recoveryType)
    }

    // Account for economizer
    ctx.usingEconomizer = this.system.isUsingEconomizer();
    let economizerOn = ctx.usingEconomizer ? ctx.h_oa <= ctx.h_return : false;
    if (economizerOn) {
      ctx.log("Economizer on (h_oa <= h_return)")
      ctx.Q_supply = ctx.Q_supply_values.getSystemSupplyCooling(mo, hr)
      ctx.F_min = ctx.eval('V_ot_hr / Q_supply', {
      }, 'F_min')
      ctx.T_supply = this.system.getCoolingSupplyTemp()
      ctx.F_econ = ctx.eval('(h_return-(0.24+0.444*W_return)*T_supply-1061*W_return) / ' +
        '((0.444*T_supply+1061)*(W_oa-W_return)+h_return-h_oa)', {
      }, 'F_econ')
      ctx.F_econ = ctx.eval('clamp(F_econ, F_min, 1.0)', {
        clamp: clamp,
      }, 'F_econ')
      ctx.h_mix = ctx.eval('F_econ*h_oa + (1.0-F_econ)*h_return', {
      }, 'h_mix')
      ctx.W_mix = ctx.eval('F_econ*W_oa+(1-F_econ)*W_return', {
      }, 'W_mix')
    } else {
      ctx.log("Economizer off")
      ctx.h_mix = ctx.eval('(h_vent*m_vent + h_return*m_recirc)/(m_vent+m_recirc)', {
      }, 'h_mix')
      ctx.W_mix = ctx.eval('(W_vent*m_vent+W_return*m_recirc)/(m_vent+m_recirc)', {
      }, 'W_mix')
    }

    // For non-recovery systems, q_plenum was excluded from T_return to prevent numerical
    // blow-up at low airflows. Apply it to the mixed-air enthalpy here so the coil load
    // fully accounts for the plenum contribution. For HRV/ERV systems the plenum is already
    // embedded in T_return (and exhausted through the recovery device), so no adjustment is needed.
    if (ctx.recoveryType === RecoveryType.None) {
      let m_supply = ctx.m_vent + ctx.m_recirc
      if (m_supply > 0) {
        ctx.h_mix = ctx.eval('h_mix + q_plenum/(60*m_supply)', {
          q_plenum: this.calc_q_plenum(mo, hr),
          m_supply: m_supply,
        }, 'h_mix')
      }
    }

    ctx.T_mix = ctx.eval('(h_mix - 1061*W_mix)/(0.24+0.444*W_mix)', {
    }, 'T_mix')

    // If T_mix has dropped below the supply temperature, the mixed air is already
    // colder than the setpoint and the cooling coil has nothing to do. This occurs
    // on cold nights when large negative plenum loads outpace low VAV airflow.
    // Return a flag so the caller can record zero load and skip psychrometrics.
    if (ctx.T_mix < ctx.T_supply_cool) {
      ctx.log(`T_mix (${ctx.T_mix.toFixed(1)}\u00b0F) is below T_supply_cool ` +
        `(${ctx.T_supply_cool.toFixed(1)}\u00b0F); no cooling load this hour.`)
      ctx.endSection("Calc mix values cooling")
      return { noLoad: true }
    }

    let mixedAirPsychrometrics = ctx.call(psy.CalcPsychrometrics, ctx.T_mix, ctx.altitude,
      PsyCalcMethod.CalcWithHumidityRatio, {
        W: ctx.W_mix,
      }
    )

    let res = {
      outdoorAirPsychrometrics: outdoorAirPsychrometrics,

      h_mix: ctx.h_mix,
      W_mix: ctx.W_mix,
      T_mix: ctx.T_mix,
      mixedAirPsychrometrics: mixedAirPsychrometrics,

      T_return: ctx.T_return,
      W_return: ctx.W_return,
      h_return: ctx.h_return,
      v_return: ctx.v_return,
      returnAirPsychrometrics: returnValues.returnAirPsychrometrics,

      m_vent: ctx.m_vent,
      m_recirc: ctx.m_recirc,
    }
    ctx.endSection("Calc mix values cooling")
    return res
  }

  async calc_q_cooling_system() {
    let ctx = this.ctx
    ctx.startSection("Calc q_cooling_system")

    ctx.q_cooling_sens = math.zeros(12, 24)
    ctx.q_cooling_lat = math.zeros(12, 24)
    ctx.psychrometrics = new MonthHourMap()

    let peakValues = ctx.Q_supply_values.findPeakSystemSupplyCooling()
    ctx.peak_Q_supply = peakValues.value
    ctx.peak_Q_supply_mo = peakValues.time[0]
    ctx.peak_Q_supply_hr = peakValues.time[1]

    ctx.v_supply = ctx.call(psy.CalcPsychrometrics, ctx.T_supply_cool, ctx.altitude,
      PsyCalcMethod.CalcWithRelativeHumidity, {
        RH: 0.95
    }).v

    for (let mo = 0; mo < 12; mo++) {
      if (valOr(this.debugOpts.calcFirstHourOnly, false) && mo != 0) {
        break;
      }
      ctx.startLocalSection("Month " + mo)
      for (let hr = 0; hr < 24; hr++) {
        if (valOr(this.debugOpts.calcFirstHourOnly, false) && hr != 0) {
          break;
        }
        ctx.startLocalSection("Hour " + hr)
        ctx.setProgressText(`Calculating system cooling for month ${mo+1}, hour ${hr+1}`)
        await ctx.briefWait()

        ctx.Q_supply = ctx.Q_supply_values.getSystemSupplyCooling(mo, hr)
        if (ctx.Q_supply == 0) {
          ctx.log("Q_supply is 0, skipping calculations")
          ctx.q_cooling_sens.set([mo, hr], 0)
          ctx.q_cooling_lat.set([mo, hr], 0)
          ctx.endSection("Hour " + hr)
          continue;
        }
        
        ctx.mix_values = this.calc_mix_values_cooling(mo, hr)
        if (ctx.mix_values.noLoad) {
          ctx.log("T_mix below T_supply_cool; setting cooling loads to 0 for this hour.")
          ctx.q_cooling_sens.set([mo, hr], 0)
          ctx.q_cooling_lat.set([mo, hr], 0)
          ctx.endSection("Hour " + hr)
          continue;
        }
        ctx.h_mix = ctx.mix_values.h_mix
        ctx.W_mix = ctx.mix_values.W_mix
        ctx.T_mix = ctx.mix_values.T_mix
        // Required by calc_q_fan_supply
        ctx.m_vent = ctx.mix_values.m_vent
        ctx.m_recirc = ctx.mix_values.m_recirc
        ctx.W_supply = this.calc_W_supply_cooling(ctx.W_mix)
        let supplyAirPsychrometrics = ctx.call(psy.CalcPsychrometrics, ctx.T_supply_cool, ctx.altitude,
          PsyCalcMethod.CalcWithHumidityRatio, {
            W: ctx.W_supply,
          }
        )

        ctx.q_fan_supply = this.calc_q_fan_supply()
        let q_sens = ctx.eval('60*0.24*Q_supply*(1.0/v_supply)*(T_mix-T_supply_cool) + ' +
          'q_fan_supply', {
        }, 'q_sens');
        ctx.q_cooling_sens.set([mo, hr], q_sens)

        ctx.T_db_avg = ctx.eval('(T_mix + T_supply_cool)/2.0', {
        }, 'T_db_avg')
        let q_lat = ctx.eval('max(60*Q_supply*(1.0/v_supply)*(W_mix-W_supply)*(1061+0.444*T_db_avg), 0)', {
          max: Math.max,
        }, 'q_lat');
        ctx.q_cooling_lat.set([mo, hr], q_lat)

        ctx.psychrometrics.set([mo, hr], {
          supplyAir: supplyAirPsychrometrics,
          returnAir: ctx.mix_values.returnAirPsychrometrics,
          outdoorAir: ctx.mix_values.outdoorAirPsychrometrics,
          mixedAir: ctx.mix_values.mixedAirPsychrometrics,
        })

        ctx.endSection("Hour " + hr)
      }
      ctx.endSection("Month " + mo)
    }

    ctx.logLoadMatrix('q_cooling_sens', ctx.q_cooling_sens)
    ctx.logLoadMatrix('q_cooling_lat', ctx.q_cooling_lat)

    let res = {
      q_cooling_sens: ctx.q_cooling_sens,
      q_cooling_lat: ctx.q_cooling_lat,
      q_cooling_total: math.add(ctx.q_cooling_sens, ctx.q_cooling_lat),
      psychrometrics: ctx.psychrometrics,
    }

    ctx.endSection()
    return res;
  }

  calc_W_supply_heating() {
    let ctx = this.ctx
    ctx.startSection("Calc W_supply")

    // Yes the "Add Winter Humidification" section of System Load Calculations doc
    // Note - for V1, we are not supporting winter humidification
    let addWinterHumidification = false;
    if (!addWinterHumidification) {
      ctx.log("No winter humidification (not yet supported). W_supply = W_mix")
      ctx.require('W_mix')
      ctx.W_supply = ctx.eval('W_mix', {}, 'W_supply')
    } else {
      ctx.assert(false, "Winter humidification not supported")
      /*
      ctx.m_supply_heat = ctx.eval('m_recirc+m_vent', {
      }, 'm_supply_heat')
      // TODO
      ctx.T_room = 0
      ctx.T_supply_heat = 0
      ctx.T_db_avg = ctx.eval('(T_room+T_supply_heat)/2.0', {
      }, 'T_db_avg')

      // TODO - fill in vars
      ctx.peak_mo = 0
      ctx.peak_hr = 0
      ctx.W_return_peak = 0
      ctx.q_lat_peak = 0
      ctx.Q_supply = 0
      ctx.v_supply = 0
      ctx.T_db_avg_peak = 0
      ctx.W_supply = ctx.eval('W_return_peak - q_lat_peak/(60*Q_supply*air_density*' +
        '(1061+0.444*T_db_avg_peak)', {
        }, 'W_supply')
      */
    }

    let res = ctx.W_supply
    ctx.endSection()
    return res
  }

  calc_mix_values_heating() {
    let ctx = this.ctx
    ctx.startSection("Calc mix values heating")

    let heatRecovery = this.system.heatRecovery;
    ctx.recoveryType = heatRecovery.recoveryType.value

    let t_db_out = this.calc_T_db_out_heating()
    let psychrometrics = ctx.call(psy.CalcPsychrometrics, t_db_out, ctx.altitude,
      PsyCalcMethod.CalcWithRelativeHumidity, {
        RH: 0.50,
      })
    ctx.h_oa = psychrometrics.h
    ctx.W_oa = psychrometrics.W
    ctx.T_oa = t_db_out

    let returnValues = this.calc_return_values_heating()
    ctx.T_return = returnValues.T_return
    ctx.W_return = returnValues.W_return
    ctx.h_return = returnValues.h_return
    ctx.v_return = returnValues.v_return
    
    ctx.V_ot = ctx.V_ot_values.get_V_ot_heating()
    ctx.m_vent = ctx.eval('V_ot*air_density', {
    }, 'm_vent')
    ctx.Q_return = ctx.Q_supply_values.getSystemSupplyHeating()
    // TODO - modified this here
    ctx.log(`TODO - modified this here to set m_recirc = max(0, ...)`)
    ctx.m_recirc = ctx.eval('max(Q_return-V_ot, 0)/v_return', {
      max: Math.max,
    }, 'm_recirc')
    if (ctx.recoveryType != RecoveryType.None) {
      // These vars are only needed if RecoveryType is not None
      ctx.Q_exhaust = heatRecovery.calc_Q_exhaust(ctx, ctx.V_ot)
      ctx.m_exhaust = ctx.eval('Q_exhaust/v_return', {
      }, 'm_exhaust')
      ctx.m_min = ctx.eval('min(m_exhaust, m_vent)', {
      }, 'm_min')
    }

    if (ctx.recoveryType == RecoveryType.None) {
      ctx.log(`Calc h_vent, T_vent, W_vent for recovery type None`)
      ctx.h_vent = ctx.h_oa
      ctx.T_vent = ctx.T_oa
      ctx.W_vent = ctx.W_oa
    } else if (ctx.recoveryType == RecoveryType.HRV) {
      ctx.log(`Calc h_vent, T_vent, W_vent for recovery type HRV`)
      let hrvInputs = heatRecovery.hrvGroup
      ctx.W_vent = ctx.W_oa
      ctx.T_vent = ctx.eval('T_oa - (epsilon_s*m_min/max(m_vent, 0.0001))*(T_oa-T_return)', {
        epsilon_s: hrvInputs.get('winterEfficiency').value / 100.0,
      }, 'T_vent')
      ctx.h_vent = ctx.eval('0.24*T_vent+W_vent*(1061+0.444*T_vent)', {
      }, 'h_vent')
    } else if (ctx.recoveryType == RecoveryType.ERV) {
      ctx.log(`Calc h_vent, T_vent, W_vent for recovery type ERV`)
      let ervInputs = heatRecovery.ervGroup
      ctx.h_vent = ctx.eval('h_oa-(epsilon_T*m_min/max(m_vent, 0.0001)*(h_oa-h_return))', {
        epsilon_T: ervInputs.get('winterTotalEfficiency').value / 100.0,
      }, 'h_vent')
      ctx.T_vent = ctx.eval('T_oa-(epsilon_s*m_min/max(m_vent, 0.0001))*(T_oa-T_return)', {
        epsilon_s: ervInputs.get('winterSensibleEfficiency').value / 100.0,
      }, 'T_vent')
      ctx.W_vent = ctx.eval('(h_vent-0.24*T_vent)/(1061+0.444*T_vent)', {
      }, 'W_vent')
    } else {
      throw new Error("Unsupported recovery type: " + ctx.recoveryType)
    }

    ctx.h_mix = ctx.eval('(m_vent*h_vent + m_recirc*h_return)/(m_vent+m_recirc)', {
    }, 'h_mix')
    ctx.W_mix = ctx.eval('(W_vent*m_vent + W_return*m_recirc)/(m_vent+m_recirc)', {
    }, 'W_mix')
    ctx.T_mix = ctx.eval('(h_mix - 1061*W_mix)/(0.24+0.444*W_mix)', {
    }, 'T_mix')

    let res = {
      h_mix: ctx.h_mix,
      W_mix: ctx.W_mix,
      T_mix: ctx.T_mix,
      m_vent: ctx.m_vent,
      m_recirc: ctx.m_recirc,
    }

    ctx.endSection()
    return res
  }

  calc_v_supply_heating() {
    let ctx = this.ctx
    ctx.startSection("Calc v_supply heating")

    let addWinterHumidification = false;
    if (!addWinterHumidification) {
      ctx.log("Add winter humidification is off (not yet supported)")
      ctx.W_supply = this.calc_W_supply_heating()
      ctx.v_supply = ctx.call(psy.CalcPsychrometrics, ctx.T_supply_heat, ctx.altitude,
        PsyCalcMethod.CalcWithHumidityRatio, {
          W: ctx.W_supply
      }).v
    } else {
      ctx.log("Add winter humidification is on")
      ctx.assert(false, "Winter humidification not supported")
      ctx.v_supply = ctx.call(psy.CalcPsychrometrics, ctx.T_supply_heat, ctx.altitude,
        PsyCalcMethod.CalcWithRelativeHumidity, {
          RH: 0.80
      }).v
    }

    let res = ctx.v_supply
    ctx.endSection()
    return res
  }

  async calc_q_heating_system() {
    let ctx = this.ctx
    ctx.startSection("Calc q_heating_system")

    // Note: For heating, q_heat does not depend on (mo, hr).
    // We return q_heating_sens, which is a single scalar value

    ctx.require('T_supply_heat')
    ctx.Q_supply = ctx.Q_supply_values.getSystemSupplyHeating()

    ctx.mixValues = this.calc_mix_values_heating()
    ctx.T_mix = ctx.mixValues.T_mix
    ctx.W_mix = ctx.mixValues.W_mix
    // Required by calc_q_fan_supply
    ctx.m_vent = ctx.mixValues.m_vent
    ctx.m_recirc = ctx.mixValues.m_recirc

    ctx.v_supply = this.calc_v_supply_heating()
    ctx.q_fan_supply = this.calc_q_fan_supply()
    ctx.q_heat = ctx.eval('60*0.24*Q_supply*(1.0/v_supply)*(T_supply_heat-T_mix)-q_fan_supply', {
    }, 'q_heat')

    let res = {
      q_heating_sens: ctx.q_heat,
    }

    ctx.endSection()
    return res;
  }

  /*
  This is called by the SpaceWorker WebWorker to calculate loads.
  Creates own CalcContext and calculates the loads independently.
  */
  static async calculateSpaceLoads_AsJob(project, system, zone, spaceType,
                                            jobData, opts) {
    opts = valOr(opts, {});
    let sendProgressUpdate = (progressText) => {
    };
    if (opts.onProgressUpdate) {
      sendProgressUpdate = opts.onProgressUpdate;
    }

    sendProgressUpdate("Calculating - 0%");
    let results = null
    let calcContext = CalcContext.create({
      skipWaits: true,
      progressUpdateIntervalSecs: 1,
      progressUpdateFunc: (ctx, progressText) => {
        sendProgressUpdate(`Calculating - ${ctx.getProgressPercent()}%`);
      }
    });
    try {
      SystemLoadCalculator.setupCalcContext(calcContext, project, system);

      let zoneInfo = Space.ZoneInfo.createFromZone(zone);

      let loadResults = null;
      let useDummySpaceLoads = valOr(jobData.debugOpts.useDummySpaceLoads, false);
      if (!useDummySpaceLoads) {
        loadResults = await spaceType.calculateLoadsAsync(calcContext, zoneInfo, {
          isForSystemLoadCalcs: true,
        })
      } else {
        console.log("Using dummy space loads");
        loadResults = spaceType.getDummyLoadResults(calcContext, {})
      }

      sendProgressUpdate("Done");
      console.log("Worker calculation complete, returning results");
      let serializedLoadResults = Space.Space.writeWorkerResultsToJson(loadResults);
      //console.log("Returning load results: ", serializedLoadResults);
      results = {
        loadResults: serializedLoadResults,
        calcLog: deepCopyObject(calcContext.getFullStructuredLog()),
        didError: false,
      }
    } catch (err) {
      // Note: we don't rethrow here b/c we'd like to return the calcLog intact.
      // Worker should ideally never throw.
      console.log(`Caught fatal error in worker, returning:\n${err}\nTRACE:\n${err.stack}`);
      calcContext.logFatalError(err);
      calcContext.cleanupSectionStack();
      sendProgressUpdate("Error during calculations");
      results = {
        loadResults: null,
        calcLog: deepCopyObject(calcContext.getFullStructuredLog()),
        didError: true,
        errMsg: err.toString(),
      }
    }

    return results;
  }

  async calcSpaceLoadResults_Serially(jobData) {
    /*
    This is used for testing. Usually we calc the loads in parallel using workers.
    */
    let system = this.system;
    let zone = system.getZoneAtIndex(jobData.zoneIndex);
    let space = zone.getSpaceAtIndex(jobData.spaceIndex);
    let spaceType = space.getSpaceType();
    let results = await SystemLoadCalculator.calculateSpaceLoads_AsJob(
        this.proj, system, zone, spaceType, jobData, {});
    return results
  }

  async calcSpaceLoadResults() {
    let ctx = this.ctx
    ctx.startSection("Space load results")

    //let spaceWorkerURL = new URL('./workers/SpaceWorker.js', import.meta.url)
    let pool = null;
    if (!IsTestEnv()) {
      pool = new WorkerPool(SpaceWorkerURL, {
        maxWorkers: 10,
        workerOpts: {
          // Must be 'module' in every environment: Vite's dev server always
          // serves workers as ES modules (regardless of this setting), and
          // vite.config.js builds the production worker in ES format too
          // (worker: { format: 'es' }) so it can code-split dynamic imports.
          // A classic ('undefined') worker can't execute the resulting
          // import/export syntax - if Rollup ever needs to share a chunk
          // between this worker and another part of the bundle (e.g. a
          // lazily-loaded exporter), the classic worker fails outright with
          // "Uncaught SyntaxError: Unexpected token 'export'".
          type: "module"
        }
      });
    } else {
      console.log("Using MockWorkerPool, for testing");
      pool = new MockWorkerPool();
      pool.registerFunction('calcSpaceLoadResults', async (jobData) => {
        return await this.calcSpaceLoadResults_Serially(jobData);
      })
    }

    let spaceCalculationPromises = []

    // Start progress section
    let spaceProgressItems = []
    this.system.forEachSpace((space) => {
      spaceProgressItems.push({
        name: `Zone ${space.zoneIndex}, Space ${space.spaceIndex} (${space.spaceType.name.value})`
      })
    });
    ctx.startParallelProgressSection('Spaces', spaceProgressItems);

    // Queue all jobs
    let allWorkersSucceeded = true;
    let firstFailedJobName = null;
    let firstFailedErrMsg = null;
    for (let zoneIndex = 0; zoneIndex < this.system.zones.length; zoneIndex++) {
      let zone = this.system.zones[zoneIndex]
      let zoneResult = new ZoneResult(zone, zone.spaces.length)
      this.zoneResults.setZoneResult(zoneIndex, zoneResult)
      for (let spaceIndex = 0; spaceIndex < zone.spaces.length; spaceIndex++) {
        let space = zone.spaces[spaceIndex]
        let spaceType = space.getSpaceType()

        let jobData = {
          systemId: this.system.id,
          zoneIndex: zoneIndex,
          spaceIndex: spaceIndex,
          projectData: deepCopyObject(this.proj.getProjectJson()),
          debugOpts: deepCopyObject(this.debugOpts),
        }
        let thisSpaceIndex = spaceIndex;
        let jobName = `Zone ${zoneIndex}, Space ${spaceIndex} (${spaceType.name.value})`
        let jobOptions = {
          on: (payload) => {
            if (payload.type == 'progress') {
              ctx.setParallelProgress(jobName, payload.progressText);
            }
          }
        };
        let spaceCalculationPromise = pool.exec('calcSpaceLoadResults', [jobData],  jobOptions)
          .then((jobResults) => {
            console.log(`Finished space calculation for Zone ${zoneIndex}, Space ${spaceIndex} (${spaceType.name.value})`);
            let spaceResult = new SpaceResult(space)
            let loadResults = null;
            if (!jobResults.didError) {
              loadResults = Space.Space.readWorkerResultsFromJson(jobResults.loadResults)
              //console.log(`Read load results for Zone ${zoneIndex}, Space ${spaceIndex} (${spaceType.name.value}): `, loadResults);
            }
            spaceResult.setLoadResults(loadResults)
            // TODO HERE - these used to be added to the calc log, but was causing huge memory use.
            // Maybe have a way to only do it in debug mode?
            //spaceResult.setCalcLog(jobResults.calcLog)
            zoneResult.setSpaceResult(thisSpaceIndex, spaceResult)
            ctx.log(`Done (${jobResults.didError ? 'Error' : 'Success' }) - Zone ${zoneIndex}, Space ${spaceIndex} (${spaceType.name.value})`);
            if (jobResults.didError) {
              allWorkersSucceeded = false;
              if (firstFailedJobName === null) {
                firstFailedJobName = jobName;
                firstFailedErrMsg = jobResults.errMsg;
              }
            }
          }).catch((err) => {
            console.error(`Exception in worker for Zone ${zoneIndex} Space ${spaceIndex} (${spaceType.name.value}): `, err);
            let newErrorMsg = `Exception in worker for Zone ${zoneIndex}, Space ${spaceIndex} (${spaceType.name.value}): ${err?.message ?? err}`
            ctx.log(`Exception - Zone ${zoneIndex}, Space ${spaceIndex} (${spaceType.name.value})`);
            throw new Error(newErrorMsg);
          })
        spaceCalculationPromises.push(spaceCalculationPromise)
      }
    }

    // Wait for all jobs to finish.
    // Note: we let all workers finish, even if some error, so that we can get their logs.
    // We throw at the end if any worker errored.
    // Use allSettled (not all) so we always wait for every job before terminating
    // the pool below - terminating while sibling jobs are still pending in the
    // pool causes their promises to reject with no error object (undefined),
    // masking whatever actually failed.
    let settledResults;
    try {
      ctx.log("Waiting for all space calculations to finish...")
      settledResults = await Promise.allSettled(spaceCalculationPromises);
    } finally {
      // Close the pool
      await pool.terminate();
    }
    ctx.log("Done all")
    ctx.endProgressSection();

    // Surface the first real exception now, if any space calculation threw.
    let firstRejection = settledResults.find(r => r.status === 'rejected');
    if (firstRejection) {
      throw firstRejection.reason;
    }

    // Log results
    this.system.forEachSpace((space) => {
      ctx.startLocalSection(`Zone ${space.zoneIndex}, Space ${space.spaceIndex} (${space.spaceType.name.value})`)

      let spaceResult = this.zoneResults.getZoneResult(space.zoneIndex).getSpaceResult(space.spaceIndex);

      // TODO HERE
      //let calcLog = spaceResult.getCalcLog();
      //ctx.pushFullStructuredLog(calcLog)

      let loadResults = spaceResult.getLoadResults();
      //console.log("Load results: ", loadResults);
      if (loadResults) {
        ctx.logValue('heating, q_sensible', loadResults.heating.q_sensible)
        ctx.logValue('heating, q_total', loadResults.heating.q_total)
        ctx.logLoadMatrix('cooling, q_sensible', loadResults.cooling.q_sensible)
        ctx.logLoadMatrix('cooling, q_latent', loadResults.cooling.q_latent)
        ctx.logLoadMatrix('cooling, q_total', loadResults.cooling.q_total)
        ctx.logLoadMatrix('plenum_loads', loadResults.plenum_loads)
      }

      ctx.endSection()
    });

    // Throw if any worker errored
    if (!allWorkersSucceeded) {
      throw new Error(`Load calculations failed for ${firstFailedJobName}. Please verify your inputs for this space. Error details: ${firstFailedErrMsg}`)
    }

    // Validate space loads - throw error if any space has 0 heating or cooling load
    // Later calculations will fail if loads are 0, so better to error out here.
    this.validateSpaceLoads(ctx)

    // Calculate zone totals
    this.zoneResults.calculateZoneTotals(ctx)

    ctx.endSection()
  }

  validateSpaceLoads(ctx) {
    let ctx2 = ctx
    ctx2.startSection("Validate space loads")
    
    this.system.forEachSpace((space) => {
      ctx2.startLocalSection(`Zone ${space.zoneIndex}, Space ${space.spaceIndex} (${space.spaceType.name.value})`)
      
      let spaceResult = this.zoneResults.getSpaceResult(space.zoneIndex, space.spaceIndex);
      let loadResults = spaceResult.getLoadResults();
      
      if (loadResults) {
        let heatingLoad = loadResults.heating.q_total;
        let peakCoolingInfo = findMaxLoadInMatrix(loadResults.cooling.q_total);
        let peakCoolingLoad = peakCoolingInfo.value;
        if (heatingLoad === 0 && peakCoolingLoad === 0) {
          throw new Error(`Space '${space.spaceType.name.value}' in Zone '${space.zone.getName()}' has no heating or cooling loads. Please check that it is filled out properly, then re-run calculations.`);
        }
        
        ctx2.log(`Heating load: ${formatNum(heatingLoad)} BTU/hr, Peak cooling load: ${formatNum(peakCoolingLoad)} BTU/hr`);
      } else {
        throw new Error(`Space "${space.spaceType.name.value}" in Zone ${space.zoneIndex} has no load results.`);
      }
      
      ctx2.endSection()
    });
    
    ctx2.endSection()
  }

  calcSystemOccupancySchedule() {
    let ctx = this.ctx
    ctx.startSection("System occupancy schedule")
    let systemOccupancySchedule = makeVector(24).fill(0.0)
    for (let hr = 0; hr < 24; hr++) {
      systemOccupancySchedule[hr] = this.system.getFracOccupied(hr);
    }
    ctx.logValue('systemOccupancySchedule', systemOccupancySchedule)
    ctx.endSection()
    return systemOccupancySchedule;
  }

  applySystemSafetyFactors() {
    let ctx = this.ctx
    ctx.startSection("Apply safety factors")

    let safetyFactors = this.system.safetyFactors
    ctx.sensibleGainsFactor = 1 + safetyFactors.sensibleGains.value / 100
    ctx.latentGainsFactor = 1 + safetyFactors.latentGains.value / 100
    ctx.sensibleLossesFactor = 1 + safetyFactors.sensibleLosses.value / 100

    ctx.system_cooling.q_cooling_sens = ctx.eval(
      'q_cooling_sens * sensibleGainsFactor',
      { q_cooling_sens: ctx.system_cooling.q_cooling_sens },
      'q_cooling_sens'
    )
    ctx.system_cooling.q_cooling_lat = ctx.eval(
      'q_cooling_lat * latentGainsFactor',
      { q_cooling_lat: ctx.system_cooling.q_cooling_lat },
      'q_cooling_lat'
    )
    ctx.system_cooling.q_cooling_total = ctx.eval(
      'q_cooling_sens + q_cooling_lat',
      {
        q_cooling_sens: ctx.system_cooling.q_cooling_sens,
        q_cooling_lat: ctx.system_cooling.q_cooling_lat,
      },
      'q_cooling_total'
    )
    ctx.system_heating.q_heating_sens = ctx.eval(
      'q_heating_sens * sensibleLossesFactor',
      { q_heating_sens: ctx.system_heating.q_heating_sens },
      'q_heating_sens'
    )

    ctx.endSection()
  }

  async calcAllOutputs() {
    let ctx = this.ctx
    ctx.startLocalSection("Calc outputs")
    ctx.startProgressSection('System loads', {
      'Space loads': {},
      'Space ventilations': {},
      'System cooling': {},
      'System heating': {},
    })

    ctx.setProgress("Space loads")
    await this.calcSpaceLoadResults()

    ctx.systemOccupancySchedule = this.calcSystemOccupancySchedule()

    ctx.setProgress("Space ventilations")
    ctx.spaceVentilations = this.calcSpaceVentilations()

    ctx.T_supply_cool = this.system.getCoolingSupplyTemp()
    ctx.T_supply_heat = this.system.getHeatingSupplyTemp()
    ctx.V_ot_values = this.calc_V_ot_values()
    // Note - Q_supply values depend on V_ot values, so must be calculated after.
    ctx.Q_supply_values = this.calc_Q_supply()
    ctx.q_plenum_matrix = this.calc_q_plenum_matrix()

    ctx.setProgress("System cooling")
    ctx.system_cooling = await this.calc_q_cooling_system()
    ctx.setProgress("System heating")
    ctx.system_heating = await this.calc_q_heating_system()

    // Apply safety factors
    this.applySystemSafetyFactors()

    let res = {
      zoneResults: this.zoneResults,
      V_ot_values: ctx.V_ot_values,
      Q_supply_values: ctx.Q_supply_values,
      system_cooling: ctx.system_cooling,
      system_heating: ctx.system_heating,
    }

    // Long wait so the progress UI can update
    await ctx.longWait(2);

    ctx.endProgressSection()

    ctx.endSection()
    return res;
  }

  /*
  This is static b/c it is reused by the SpaceWorkers
  */
  static setupCalcContext(ctx, proj, system) {
    ctx.startLocalSection("Basic")

    // Tables cache
    let tablesCache = new DataCache();
    MaterialDataTableRegistry.registerCommercialTables(tablesCache);
    ctx.tablesCache = tablesCache;

    // Building and env:
    let locationData = proj.buildingAndEnv.getLocationData();
    // Put the location data in 'toplevelData' for compatibility with residential calculations
    ctx.toplevelData = {
      locationData: locationData.getOutputs(),
      dayOfMonth: 21,
    }
    ctx.buildingAndEnv = proj.buildingAndEnv.getOutputs();

    // TODO - is air_density_0 correct?
    ctx.air_density_0 = 0.074
    ctx.altitude  = ctx.toplevelData.locationData.elevation;
    ctx.P_loc = ctx.call(psy.calcP_loc, ctx.altitude)
    ctx.P_atm = psy.P_std
    ctx.air_density = ctx.eval('air_density_0 * P_loc / P_atm', {}, 'air_density')

    // Design temps:
    ctx.designTemps = system.designTempInputs.getDesignTemps(ctx);

    ctx.endSection()
  }

  makeSystemInputSummary(outputs) {
    let ctx = this.ctx;

    let weatherData = ctx.toplevelData.locationData;

    let environment = {
      name: "Environment",
      type: "OutputValueList",
      outputValues: [
        OutputValue.createSimple("Location", FieldType.String, weatherData.locationName),
        OutputValue.createSimple("Latitude", FieldType.Angle, weatherData.latitude, {min: -90, max: 90}),
        OutputValue.createSimple("Longitude", FieldType.Angle, weatherData.longitude, {min: -180, max: 180}),
        OutputValue.createSimple("Elevation", FieldType.Length, weatherData.elevation, {min: -1e9}),
      ]
    };

    let designHeatingTempStr = ctx.designTemps.getHeatingDesignTempStr();
    let designHeatingTempValueStr = ctx.designTemps.getHeatingDesignTempValueStr();
    let designCoolingTempStr = ctx.designTemps.getCoolingDesignTempStr();
    let designTemps = {
      name: "Design Temperatures",
      type: "OutputValueList",
      outputValues: [
        OutputValue.createSimple("Design Heating Temp", FieldType.String, `${designHeatingTempStr}, ${designHeatingTempValueStr}`),
        OutputValue.createSimple("Design Cooling Temp", FieldType.String, `${designCoolingTempStr}`),
      ]
    }

    let temperatureChartsSection = {
      name: "Design Temperature Charts",
      type: "ResultsNode",
      resultsNode: this.makeTemperatureChartsNode(outputs),
    };

    let inputs = {
      name: "Inputs",
      type: "OutputValueList",
      outputValues: [
        ...(this.system.usesAutoVentilation() ? [OutputValue.createFromField(this.system.estimatedTotalOccupancy, {name: 'System Occupancy'})] : []),
        OutputValue.createFromField(this.system.demandControlledVentilation.get('use'), {name: 'Demand Controlled Ventilation'}),
        OutputValue.createFromField(this.system.economizer.get('use'), {name: 'Economizer'}),
        OutputValue.createFromField(this.system.heatRecovery.recoveryType),
      ],
    };
    let recoveryType = this.system.heatRecovery.recoveryType.value;
    if (recoveryType == RecoveryType.HRV) {
      let hrvgroup = this.system.heatRecovery.hrvGroup;
      let fields = [
        hrvgroup.get('summerEfficiency'),
        hrvgroup.get('winterEfficiency'),
      ];
      for (let field of fields) {
        inputs.outputValues.push(OutputValue.createFromField(field));
      }
    } else if (recoveryType == RecoveryType.ERV) {
      let ervGroup = [
        this.system.heatRecovery.ervGroup.get('summerTotalEfficiency'),
        this.system.heatRecovery.ervGroup.get('summerSensibleEfficiency'),
        this.system.heatRecovery.ervGroup.get('winterTotalEfficiency'),
        this.system.heatRecovery.ervGroup.get('winterSensibleEfficiency'),
      ];
      for (let field of ervGroup) {
        inputs.outputValues.push(OutputValue.createFromField(field));
      }
    } else if (recoveryType == RecoveryType.None) {
      // No additional fields
    } else {
      throw new Error("Unsupported recovery type: " + recoveryType)
    }
    inputs.outputValues.push(OutputValue.createFromField(this.system.safetyFactors.sensibleGains, {name: 'Sensible Cooling Safety Factor'}));
    inputs.outputValues.push(OutputValue.createFromField(this.system.safetyFactors.latentGains, {name: 'Latent Cooling Safety Factor'}));
    inputs.outputValues.push(OutputValue.createFromField(this.system.safetyFactors.sensibleLosses, {name: 'Heating Safety Factor'}));

    // Removed for brevity
    /*
    let zoneList = {
      name: "Zones List",
      type: "Table",
      columns: [
        {name: "Zone Name"},
        {name: "Summer Indoor Temp"},
        {name: "Winter Indoor Temp"},
        {name: "Humidity Setpoint"},
      ],
      data: [],
    };
    this.system.forEachZone((zone) => {
      let zoneData = [
        zone.zone.name.value,
        OutputValue.createFromField(zone.zone.summerIndoorTemp),
        OutputValue.createFromField(zone.zone.winterIndoorTemp),
        OutputValue.createFromField(zone.zone.humiditySetpoint),
      ];
      zoneList.data.push({rowData: zoneData});
    });
    */

    let spaceList = {
      name: "Zones and Spaces",
      type: "Table",
      columns: [
        {name: "Space Name"},
        {name: "Quantity"},
      ],
      data: [],
    };
    this.system.forEachZone((zone) => {
      spaceList.data.push(zone.zone.name.value);
      for (let space of zone.zone.spaces) {
        let spaceData = [
          space.getSpaceType().name.value,
          OutputValue.createFromField(space.quantity),
        ];
        spaceList.data.push({rowData: spaceData});
      }
    });

    let designCoolingTemperatures = {
      name: "Design Cooling Temperatures (by month)",
      type: "Table",
      columns: [
        {name: "Month"},
        {name: `${ctx.designTemps.getCoolingDryBulbTempName()}`},
        {name: `${ctx.designTemps.getCoolingWetBulbTempName()}`},
      ],
      data: [],
    };

    // Populate the design cooling temperatures table
    for (let monthIndex = 0; monthIndex < 12; ++monthIndex) {
      let peakDryBulb = -Infinity;
      let peakWetBulb = -Infinity;
      
      // Find peak temperatures across all hours of the design day
      for (let hourIndex = 0; hourIndex < 24; ++hourIndex) {
        let dryBulbTemp = ctx.designTemps.getCoolingDryBulbOut(ctx, monthIndex, hourIndex);
        let wetBulbTemp = ctx.designTemps.getCoolingWetBulbOut(ctx, monthIndex, hourIndex);
        
        peakDryBulb = Math.max(peakDryBulb, dryBulbTemp);
        peakWetBulb = Math.max(peakWetBulb, wetBulbTemp);
      }
      
      let monthData = [
        getShortMonthName(monthIndex),
        OutputValue.createSimple("Dry Bulb", FieldType.Temperature, peakDryBulb, {precision: 0}),
        OutputValue.createSimple("Wet Bulb", FieldType.Temperature, peakWetBulb, {precision: 0}),
      ];
      designCoolingTemperatures.data.push({rowData: monthData});
    }

    let allSections = [
      environment,
      designTemps,
      designCoolingTemperatures,
      temperatureChartsSection,
      inputs,
      //zoneList,
      spaceList,
    ];
    return {
      allSections: allSections,

      environment: environment,
      designTemps: designTemps,
      designCoolingTemperatures: designCoolingTemperatures,

      //zoneList: zoneList,
      spaceList: spaceList,
    };
  }

  getSpacesUsedInSystem() {
    let spaces = [];
    let spaceTypesVisited = {};
    this.system.forEachSpace((space) => {
      let spaceType = space.spaceType;
      if (spaceType.id in spaceTypesVisited) {
        // Already covered
        return;
      }
      spaceTypesVisited[spaceType.id] = true;
      spaces.push(space);
    });
    return spaces;
  }

  makeSpaceInputSummary() {
    let spaces = {
      name: "Spaces",
      type: "WideTable",
      columns: [
        {name: "Space Name"},
        {name: `Floor Area`},
        {name: "Occupancy"},
        {name: "V<sub>bz</sub>"},
        {name: "V<sub>oz</sub> (Cooling)"},
        {name: "V<sub>oz</sub> (Heating)"},
        {name: `Wall Area`},
        {name: `Roof Area`},
        {name: `Window Area`},
        {name: `Door Area`},
        {name: `Skylight Area`},
        {name: `# Appliances`},
        //{name: `Lighting Load`},
        {name: `Miscellaneous Load`},
        {name: `Winter Infiltration`},
      ],
      data: [],
    };
    let spacesUsed = this.getSpacesUsedInSystem();
    for (const space of spacesUsed) {
      let spaceType = space.spaceType;
      let spaceVentilations = this.ctx.spaceVentilations.getValuesForSpaceType(spaceType);
      let spaceData = [
        spaceType.name.value,
        OutputValue.createFromField(spaceType.floorArea),
        OutputValue.createSimple("Occupancy", FieldType.Count, spaceType.getNumOccupants()),
        OutputValue.createSimple("V<sub>bz</sub>", FieldType.AirFlow, spaceVentilations.V_bz),
        OutputValue.createSimple("V<sub>oz</sub> (Cooling)", FieldType.AirFlow, spaceVentilations.V_oz_cooling),
        OutputValue.createSimple("V<sub>oz</sub> (Heating)", FieldType.AirFlow, spaceVentilations.V_oz_heating),
        OutputValue.createSimple("Wall Area", FieldType.Area, spaceType.getTotalWallArea(), {allowMin: true}),
        OutputValue.createSimple("Roof Area", FieldType.Area, spaceType.getTotalRoofArea(), {allowMin: true}),
        OutputValue.createSimple("Window Area", FieldType.Area, spaceType.getTotalWindowArea(), {allowMin: true}),
        OutputValue.createSimple("Door Area", FieldType.Area, spaceType.getTotalDoorArea(), {allowMin: true}),
        OutputValue.createSimple("Skylight Area", FieldType.Area, spaceType.getTotalSkylightArea(), {allowMin: true}),
        OutputValue.createSimple("# Appliances", FieldType.Count, spaceType.getNumAppliances()),
        //OutputValue.createSimple("Lighting Load", FieldType.Load, spaceType.getLightingPower()),
        OutputValue.createSimple("Miscellaneous Load", FieldType.Load, spaceType.getMiscLoad()),
        // Note: we allow negative values for infiltration, since it can be negative in some cases
        OutputValue.createSimple("Winter Infiltration", FieldType.AirFlow, spaceType.getWinterInfiltration(), {min: undefined}),
      ];
      spaces.data.push({rowData: spaceData});
    }

    let summary = [
      spaces,
    ]
    return summary;
  }

  makeMonthlyTemperaturesNode() {
    let ctx = this.ctx;
    let monthLabels = Array.from({length: 12}, (_, i) => getShortMonthName(i));
    let dryBulbValues = [];
    let wetBulbValues = [];
    
    // Get the peak dry bulb and wet bulb temperatures for each month
    for (let monthIndex = 0; monthIndex < 12; ++monthIndex) {
      let peakDryBulb = -Infinity;
      let peakWetBulb = -Infinity;
      
      // Find peak temperatures across all hours of the design day
      for (let hourIndex = 0; hourIndex < 24; ++hourIndex) {
        let dryBulbTemp = ctx.designTemps.getCoolingDryBulbOut(ctx, monthIndex, hourIndex);
        let wetBulbTemp = ctx.designTemps.getCoolingWetBulbOut(ctx, monthIndex, hourIndex);
        
        peakDryBulb = Math.max(peakDryBulb, dryBulbTemp);
        peakWetBulb = Math.max(peakWetBulb, wetBulbTemp);
      }
      
      dryBulbValues.push(peakDryBulb);
      wetBulbValues.push(peakWetBulb);
    }
    
    // Create multi-line chart data
    let chartData = {
      title: `Outdoor Temperatures (${ctx.designTemps.getCoolingDesignTempStr()}) by Month`,
      labelX: 'Month',
      labelY: `Temperature`,
      labels: monthLabels,
      units: Units.F,
      precision: 1,
      series: [
        {
          name: "Dry Bulb",
          values: dryBulbValues,
          color: "#ff7043" // orange-red for dry bulb
        },
        {
          name: "Wet Bulb",
          values: wetBulbValues,
          color: "#42a5f5" // blue for wet bulb
        }
      ],
      maxWidth: 800,
      height: 500,
      showMinMaxLabels: true
    };
    
    let graphNode = new MultiLineChartNode(chartData);
    graphNode.data.helpText = `This chart shows the peak outdoor dry-bulb and wet-bulb temperatures for each month. ` +
      `These are the maximum temperatures that occur during the design day (typically the 21st) of each month. ` +
      `These values are taken from the weather data for your location and are used in cooling load calculations.`;
    
    return graphNode;
  }

  makeHourlyTemperaturesNode(outputs) {
    // Find the month with the peak cooling load
    let peakInfo = findMaxLoadInMatrix(outputs.system_cooling.q_cooling_total);
    let peakMonth = peakInfo.time[0];
    let hourLabels = Array.from({length: 24}, (_, i) => getHourString(i));
    let dryBulbValues = [];
    let wetBulbValues = [];
    
    // Get the dry bulb and wet bulb temperatures for each hour of the peak day
    for (let hourIndex = 0; hourIndex < 24; ++hourIndex) {
      let dryBulbTemp = this.ctx.designTemps.getCoolingDryBulbOut(this.ctx, peakMonth, hourIndex);
      let wetBulbTemp = this.ctx.designTemps.getCoolingWetBulbOut(this.ctx, peakMonth, hourIndex);
      
      dryBulbValues.push(dryBulbTemp);
      wetBulbValues.push(wetBulbTemp);
    }
    
    // Create multi-line chart data
    let chartData = {
      title: `Hourly Temperatures - ${getShortMonthName(peakMonth)} ${this.ctx.toplevelData.dayOfMonth}`,
      labelX: 'Hour',
      labelY: `Temperature`,
      labels: hourLabels,
      units: Units.F,
      precision: 1,
      series: [
        {
          name: "Dry Bulb",
          values: dryBulbValues,
          color: "#ff7043" // orange-red for dry bulb
        },
        {
          name: "Wet Bulb",
          values: wetBulbValues,
          color: "#42a5f5" // blue for wet bulb
        }
      ],
      maxWidth: 800,
      height: 500,
      showMinMaxLabels: true
    };
    
    let graphNode = new MultiLineChartNode(chartData);
    graphNode.data.helpText = `This chart shows the hourly outdoor dry-bulb and wet-bulb temperatures for the day with peak cooling load, ` +
      `${getShortMonthName(peakMonth)} ${this.ctx.toplevelData.dayOfMonth}. These temperature values are used in the cooling load calculations.`;
    
    return graphNode;
  }

  makeTemperatureChartsNode(outputs) {
    let resultsNode = new ResultsNode(ResultsNodeType.VerticalSplit, 'Design Temperatures');
    resultsNode.children.push(this.makeMonthlyTemperaturesNode(outputs));
    resultsNode.children.push(this.makeHourlyTemperaturesNode(outputs));
    return resultsNode;
  }

  makeSystemCoolingLoadsByMonthNode(outputs) {
    let monthLabels = Array.from({length: 12}, (_, i) => getShortMonthName(i));
    let sensibleValues = [];
    let latentValues = [];
    let peakTimes = [];
    
    for (let monthIndex = 0; monthIndex < 12; ++monthIndex) {
      let peakInfo = findMaxLoadForMonth(outputs.system_cooling.q_cooling_total, monthIndex);
      let peakTime = peakInfo.time;
      peakTimes.push(peakTime);
      
      // Get sensible and latent values at the peak total load time
      let sensibleValue = outputs.system_cooling.q_cooling_sens.get(peakTime);
      let latentValue = outputs.system_cooling.q_cooling_lat.get(peakTime);
      
      sensibleValues.push(Units.convertValue(sensibleValue, Units.Load, Units.LoadMBH));
      latentValues.push(Units.convertValue(latentValue, Units.Load, Units.LoadMBH));
    }
    
    // Create stacked bar chart data
    let stackedBarData = {
      title: 'System Peak Cooling Loads by Month',
      units: Units.LoadMBH,
      precision: 0,
      labelX: 'Month',
      labelY: `Peak Load`,
      labels: monthLabels,
      datasets: [
        {
          label: 'Sensible',
          data: sensibleValues,
          color: SENSIBLE_COLOR
        },
        {
          label: 'Latent',
          data: latentValues,
          color: LATENT_COLOR
        }
      ],
      maxWidth: 800,
      height: 500
    };
    
    let chartNode = new StackedBarChartNode(stackedBarData);
    chartNode.data.helpText = `This chart shows the peak system cooling load for each month. ` +
      `The peak load is the maximum cooling load of any hour on the design day for that month (typically the 21st).`;
    return chartNode;
  }

  makeSystemCoolingLoadsByHourNode(outputs) {
    // Find the month with the peak cooling load
    let peakInfo = findMaxLoadInMatrix(outputs.system_cooling.q_cooling_total);
    let peakMonth = peakInfo.time[0];
    let hourLabels = Array.from({length: 24}, (_, i) => getHourString(i));
    
    // Extract sensible and latent values for each hour of the peak day
    let sensibleValues = [];
    let latentValues = [];
    
    for (let hourIndex = 0; hourIndex < 24; ++hourIndex) {
      let sensibleValue = outputs.system_cooling.q_cooling_sens.get([peakMonth, hourIndex]);
      let latentValue = outputs.system_cooling.q_cooling_lat.get([peakMonth, hourIndex]);
      
      sensibleValues.push(Units.convertValue(sensibleValue, Units.Load, Units.LoadMBH));
      latentValues.push(Units.convertValue(latentValue, Units.Load, Units.LoadMBH));
    }
    
    // Create stacked bar chart data
    let stackedBarData = {
      title: `System Cooling Loads on Peak Day - ${getShortMonthName(peakMonth)} ${this.ctx.toplevelData.dayOfMonth}`,
      units: Units.LoadMBH,
      precision: 0,
      labelX: 'Hour',
      labelY: `Load`,
      labels: hourLabels,
      datasets: [
        {
          label: 'Sensible',
          data: sensibleValues,
          color: SENSIBLE_COLOR
        },
        {
          label: 'Latent',
          data: latentValues,
          color: LATENT_COLOR
        }
      ],
      maxWidth: 800,
      height: 500
    };
    
    let chartNode = new StackedBarChartNode(stackedBarData);
    chartNode.data.helpText = `This chart shows the hourly system cooling loads on the day of peak cooling load, ` +
      `${getShortMonthName(peakMonth)} ${this.ctx.toplevelData.dayOfMonth}.`;
    
    return chartNode;
  }

  makeSystemResultsNode(outputs) {
    let resultsNode = new ResultsNode(ResultsNodeType.VerticalSplit, 'System Results');

    resultsNode.children.push(this.makeSystemCoolingLoadsByMonthNode(outputs));
    resultsNode.children.push(this.makeSystemCoolingLoadsByHourNode(outputs));
    resultsNode.children.push(this.makeTotalCoolingLoadMonthHourGrid(outputs, {
      name: 'System Cooling Loads Heatmap'
    }));

    // Include solar charts related to the first day.
    // We retrieve the solar values from any spaces load results (should be the same for all of them)
    let firstSpaceLoadResults = this.zoneResults.getZoneResult(0).getSpaceResult(0).getLoadResults();
    let peakMonth = findMaxLoadInMatrix(outputs.system_cooling.q_cooling_total).time[0];
    let solarNodes = SpaceCoolingResultsNode.makeSolarChartNodes(firstSpaceLoadResults.cooling, peakMonth);
    resultsNode.children.push(...solarNodes);

    return resultsNode;
  }

  makePsychrometricsChartNode(outputs) {
    // We want the cooling psychrometrics values at the time of peak cooling
    // load.
    let peakCoolingTime = findMaxLoadInMatrix(outputs.system_cooling.q_cooling_total).time;
    let peakCoolingTimeStr = getMonthHourString(peakCoolingTime[0], peakCoolingTime[1]);
    let psychrometricsValues = outputs.system_cooling.psychrometrics.get(peakCoolingTime);

    let supplyAir = psy.ChartPoint.createFromOutputs(psychrometricsValues.supplyAir, {
      name: 'Supply Air',
    });
    let returnAir = psy.ChartPoint.createFromOutputs(psychrometricsValues.returnAir, {
      name: 'Return Air',
    });
    let outdoorAir = psy.ChartPoint.createFromOutputs(psychrometricsValues.outdoorAir, {
      name: 'Outdoor Air',
    });
    let mixedAir = psy.ChartPoint.createFromOutputs(psychrometricsValues.mixedAir, {
      name: 'Mixed Air',
    });
    let chartPoints = [
      supplyAir,
      returnAir,
      outdoorAir,
      mixedAir,
    ];
    let chartData = {
      points: chartPoints,
      altitude: this.ctx.altitude,
      helpText: `These are the air states during the time of peak cooling load (${peakCoolingTimeStr}).`,
    }
    let resultsNode = new PsychrometricsChartNode(chartData);
    return resultsNode;
  }

  makeOutputSummary(outputs, opts) {
    opts = valOr(opts, {});

    let general = {
      name: "General",
      type: "OutputValueList",
      outputValues: [
        OutputValue.createSimple("Project Name", FieldType.String, this.proj.getName()),
        OutputValue.createSimple("Calculated on", FieldType.String, this.ctx.startDateStr),
      ]
    };

    let ctx = this.ctx;
    //let designHeatingTempStr = ctx.designTemps.getHeatingDesignTempStr();
    //let designCoolingTempStr = ctx.designTemps.getCoolingDesignTempStr();

    let peakCoolingInfo = findMaxLoadInMatrix(outputs.system_cooling.q_cooling_total);
    let peakCoolingTime = peakCoolingInfo.time;
    let peakCoolingTimeStr = getMonthHourString(peakCoolingTime[0], peakCoolingTime[1]);
    let peakCoolingValue = peakCoolingInfo.value;
    let sensibleLoadAtPeak = outputs.system_cooling.q_cooling_sens.get(peakCoolingTime);
    let latentLoadAtPeak = outputs.system_cooling.q_cooling_lat.get(peakCoolingTime);
    let sensibleRatioAtPeak = sensibleLoadAtPeak / peakCoolingValue;

    // Peak sensible and latent portions of the cooling load
    let peakSensibleLoadInfo = findMaxLoadInMatrix(outputs.system_cooling.q_cooling_sens);
    let timeOfPeakSensibleLoadStr = getMonthHourString(peakSensibleLoadInfo.time[0], peakSensibleLoadInfo.time[1]);
    let peakLatentLoadInfo = findMaxLoadInMatrix(outputs.system_cooling.q_cooling_lat);
    let timeOfPeakLatentLoadStr = getMonthHourString(peakLatentLoadInfo.time[0], peakLatentLoadInfo.time[1]);

    // Airflow values
    let totalHeatingAirflow = outputs.Q_supply_values.getSystemSupplyHeating();
    let peakCoolingAirflowInfo = outputs.Q_supply_values.findPeakSystemSupplyCooling();
    let peakCoolingAirflow = peakCoolingAirflowInfo.value;

    let coilLoadHelpText = `The system heating load is calculated once, using the heating design temperature. The system cooling load is ` +
      `calculated for every hour of one day of each month (typically the 21st), for a total of 12x24 samples. The peak cooling load is the maximum across these samples.`;
    let coilLoadSummary = {
      name: "System Load Summary",
      type: "OutputValueList",
      outputValues: [
        OutputValue.createSimple("System Type", FieldType.String, this.system.getSystemType(), {bold: true}),
        OutputValue.createSimple("Peak Cooling Load", FieldType.Load,
          peakCoolingValue, {
            bold: true,
            precision: 0,
          }),
        OutputValue.createSimple("Peak Heating Load", FieldType.Load,
          outputs.system_heating.q_heating_sens, {
            bold: true,
            precision: 0,
          }),
        OutputValue.createSimple("Total Cooling Airflow", FieldType.AirFlow, peakCoolingAirflow, {
          bold: true,
          precision: 0,
        }),
        OutputValue.createSimple("Total Heating Airflow", FieldType.AirFlow, totalHeatingAirflow, {
          bold: true,
          precision: 0,
        }),
      ],
      helpText: coilLoadHelpText,
    };

    let coilLoadDetails = {
      name: "System Load Details",
      type: "OutputValueList",
      outputValues: [
        OutputValue.createSimple("Time of Peak Cooling Load", FieldType.String, peakCoolingTimeStr),
        OutputValue.createSimple(`Sensible Heat Ratio`, FieldType.Ratio, sensibleRatioAtPeak),
        OutputValue.createSimple(`Peak Sensible Load (@ ${timeOfPeakSensibleLoadStr})`, FieldType.Load,
          peakSensibleLoadInfo.value, {
            startOfSection: true,
            precision: 0,
        }),
        //OutputValue.createSimple("Time of Peak Sensible Load", FieldType.String, timeOfPeakSensibleLoadStr),
        OutputValue.createSimple(`Peak Latent Load (@ ${timeOfPeakLatentLoadStr})`, FieldType.Load,
          peakLatentLoadInfo.value, {
            precision: 0,
        }),
        //OutputValue.createSimple("Time of Peak Latent Load", FieldType.String, timeOfPeakLatentLoadStr),
        OutputValue.createSimple("Cooling Supply Temp", FieldType.Temperature,
          this.system.getCoolingSupplyTemp()),
        OutputValue.createSimple("Heating Supply Temp", FieldType.Temperature,
          this.system.getHeatingSupplyTemp()),
      ],
    };

    // Note: we either calculate V_ot (auto path), or the user provides it manually.
    let outdoorAirDetails = []
    if (this.system.usesAutoVentilation()) {
      let V_ou_cooling = outputs.V_ot_values.get_V_ou_cooling();
      let D_cooling = outputs.V_ot_values.get_D_cooling();
      let E_v_cooling = outputs.V_ot_values.get_E_v_cooling();
      outdoorAirDetails = [
        OutputValue.createSimple("V<sub>ou</sub>", FieldType.AirFlow, V_ou_cooling, {
          plainLabel: `V_ou`,
          precision: 0,
        }),
        OutputValue.createSimple("Diversity", FieldType.Ratio, D_cooling),
        OutputValue.createSimple("Ventilation Efficiency (E<sub>v</sub>)", FieldType.Ratio, E_v_cooling, {
          plainLabel: `Ventilation Efficiency (E_v)`
        }),
      ]
    }
    let V_ot_cooling_max = outputs.V_ot_values.get_V_ot_cooling_max();
    let V_ot_heating = outputs.V_ot_values.get_V_ot_heating();
    let V_ot_display = Math.max(V_ot_cooling_max, V_ot_heating);
    let outdoorAir = {
      name: "Outdoor Air",
      type: "OutputValueList",
      outputValues: [
        ...outdoorAirDetails,
        OutputValue.createSimple("Outdoor Air Required (V<sub>ot</sub>)", FieldType.AirFlow, V_ot_display, {
          plainLabel: `Outdoor Air Required (V_ot)`,
          precision: 0,
          bold: true,
        }),
      ],
    };

    let psychrometricsChartSection = {
      name: "Psychrometrics Chart",
      type: "ResultsNode",
      resultsNode: this.makePsychrometricsChartNode(outputs),
    }

    let res = []
    if (valOr(opts.includeGeneralInfo, false)) {
      res.push(general);
    }
    res.push(coilLoadSummary);
    res.push(coilLoadDetails);
    res.push(outdoorAir);
    res.push(psychrometricsChartSection);

    return {
      allSections: res,

      coilLoadSummary: coilLoadSummary,
      coilLoadDetails: coilLoadDetails,
      outdoorAir: outdoorAir,
      psychrometricsChartSection: psychrometricsChartSection,
    }
  }

  makeZoneLoadsBreakdownTable(outputs) {
    let zoneLoadBreakdown = {
      name: "Zone Loads",
      showName: false,
      type: "Table",
      columns: [
        {name: "Zone"},
        {name: "Peak Cooling Airflow"},
        {name: "Peak Heating Airflow"},
        {name: "Peak Sensible Load"},
        {name: "Peak Latent Load"},
        {name: "Peak Cooling Load"},
        {name: "Time of Peak Cooling"},
        {name: "Peak Heating Load"},
      ],
      data: [],
    };
    this.system.forEachZone((zone) => {
      let zoneCoolingAirflow = outputs.Q_supply_values.getZoneSupplyCoolingPeak(zone.zoneIndex);
      let zoneHeatingAirflow = outputs.Q_supply_values.getZoneSupplyHeating(zone.zoneIndex);
      let zoneResult = this.zoneResults.getZoneResult(zone.zoneIndex);
      let zoneTotals = zoneResult.getZoneTotals();
      let zonePeakSensibleCooling = findMaxLoadInMatrix(zoneTotals.cooling.q_sensible).value;
      let zonePeakLatentCooling = findMaxLoadInMatrix(zoneTotals.cooling.q_latent).value;
      let peakCoolingLoad = findMaxLoadInMatrix(zoneTotals.cooling.q_total);
      let timeOfZonePeakCoolingLoad = getMonthHourString(
        ...peakCoolingLoad.time
      );
      let zoneHeatingLoad = zoneTotals.heating.q_total;

      let zoneData = [
        zone.zone.name.value,
        OutputValue.createSimple("Peak Cooling Airflow", FieldType.AirFlow, zoneCoolingAirflow, {
            precision: 0,
          }),
        OutputValue.createSimple("Peak Heating Airflow", FieldType.AirFlow, zoneHeatingAirflow, {
            precision: 0,
          }),
        OutputValue.createSimple("Peak Sensible Load", FieldType.Load,
          zonePeakSensibleCooling, {
            precision: 0,
          }),
        OutputValue.createSimple("Peak Latent Load", FieldType.Load,
          zonePeakLatentCooling, {
            precision: 0,
          }),
        OutputValue.createSimple("Peak Cooling Load", FieldType.Load,
          peakCoolingLoad.value, {
            precision: 0,
          }),
        OutputValue.createSimple("Time of Peak Cooling", FieldType.String,
          timeOfZonePeakCoolingLoad),
        OutputValue.createSimple("Peak Heating Load", FieldType.Load,
          zoneHeatingLoad, {
            precision: 0,
          }),
      ];
      zoneLoadBreakdown.data.push({rowData: zoneData});
    });

    return zoneLoadBreakdown;
  }

  makeSpaceLoadsBreakdownTable(outputs) {
    let spaceLoadBreakdown = {
      name: "Space Loads",
      showName: false,
      type: "Table",
      columns: [
        {name: "Space"},
        {name: "Peak Cooling Airflow"},
        {name: "Peak Heating Airflow"},
        {name: "Peak Sensible Load"},
        {name: "Peak Latent Load"},
        {name: "Peak Cooling Load"},
        {name: "Time of Peak Cooling"},
        {name: "Peak Heating Load"},
      ],
      data: [],
    };
    this.system.forEachZone((zone) => {
      spaceLoadBreakdown.data.push(zone.zone.name.value);
      this.system.forEachSpaceInZone(zone.zoneIndex, (space) => {
        let coolingAirflow = outputs.Q_supply_values.getSpaceSupplyCoolingPeak(space.zoneIndex, space.spaceIndex);
        let heatingAirflow = outputs.Q_supply_values.getSpaceSupplyHeating(space.zoneIndex, space.spaceIndex);
        let spaceResult = this.zoneResults.getZoneResult(space.zoneIndex).getSpaceResult(space.spaceIndex);
        let loadResults = spaceResult.getLoadResults();
        let peakSensibleCooling = findMaxLoadInMatrix(loadResults.cooling.q_sensible).value;
        let peakLatentCooling = findMaxLoadInMatrix(loadResults.cooling.q_latent).value;
        let peakCoolingLoad = findMaxLoadInMatrix(loadResults.cooling.q_total);
        let timeOfPeakCoolingLoad = getMonthHourString(
          ...peakCoolingLoad.time
        );
        let heatingLoad = loadResults.heating.q_total;

        let nameStr = space.spaceType.name.value
        if (space.quantity > 1) {
          nameStr += ` (x${space.quantity})`;
        }
        let spaceData = [
          nameStr,
          OutputValue.createSimple("Peak Cooling Airflow", FieldType.AirFlow, coolingAirflow, {
              precision: 0,
            }),
          OutputValue.createSimple("Peak Heating Airflow", FieldType.AirFlow, heatingAirflow, {
              precision: 0,
            }),
          OutputValue.createSimple("Peak Sensible Load", FieldType.Load,
            peakSensibleCooling, {
              precision: 0,
            }),
          OutputValue.createSimple("Peak Latent Load", FieldType.Load,
            peakLatentCooling, {
              precision: 0,
            }),
          OutputValue.createSimple("Peak Cooling Load", FieldType.Load,
            peakCoolingLoad.value, {
              precision: 0,
            }),
          OutputValue.createSimple("Time of Peak Cooling", FieldType.String,
            timeOfPeakCoolingLoad),
          OutputValue.createSimple("Peak Heating Load", FieldType.Load,
            heatingLoad, {
              precision: 0,
            }),
        ];
        spaceLoadBreakdown.data.push({rowData: spaceData});
      });
    });

    return spaceLoadBreakdown;
  }

  makeSpaceVentilationsTable(outputs) {
    let spaceVentilations = {
      name: "Space Ventilations",
      showName: false,
      type: "Table",
      columns: [
        {name: "Space"},
        {name: "Occupancy"},
        {name: "V<sub>bz</sub>", plainName: "V_bz"},
        {name: "V<sub>oz</sub> (Cooling)"},
        {name: "V<sub>oz</sub> (Heating)"},
      ],
      data: [],
    };
    this.system.forEachZone((zone) => {
      spaceVentilations.data.push(zone.zone.name.value);
      this.system.forEachSpaceInZone(zone.zoneIndex, (space) => {
        let spaceType = space.spaceType;
        let ventilations = this.ctx.spaceVentilations.getValuesForSpaceType(spaceType);

        let nameStr = space.spaceType.name.value
        if (space.quantity > 1) {
          nameStr += ` (x${space.quantity})`;
        }
        let spaceData = [
          nameStr,
          OutputValue.createSimple("Occupancy", FieldType.Count, spaceType.getNumOccupants(), {
          }),
          OutputValue.createSimple("V<sub>bz</sub>", FieldType.AirFlow, ventilations.V_bz, {
            precision: 0,
          }),
          OutputValue.createSimple("V<sub>oz</sub> (Cooling)", FieldType.AirFlow, ventilations.V_oz_cooling, {
            precision: 0,
          }),
          OutputValue.createSimple("V<sub>oz</sub> (Heating)", FieldType.AirFlow, ventilations.V_oz_heating, {
            precision: 0,
          }),
        ];
        spaceVentilations.data.push({rowData: spaceData});
      });
    });

    return spaceVentilations;
  }

  makeZoneResultsNode(outputs, zoneIndex) {
    // Create a vertical split node to contain all sections
    let verticalSplitNode = new ResultsNode(ResultsNodeType.VerticalSplit, 'VerticalSplit');
    
    let zone = this.system.getZoneAtIndex(zoneIndex);
    
    // Add the charts
    verticalSplitNode.children.push(this.makeZoneCoolingLoadsByMonthNode(outputs, zoneIndex));
    verticalSplitNode.children.push(this.makeZoneCoolingLoadsByHourNode(outputs, zoneIndex));
    verticalSplitNode.children.push(this.makeZoneTotalCoolingLoadMonthHourGrid(outputs, zoneIndex));
    verticalSplitNode.children.push(this.makeZoneSpaceSunburstChart(outputs, zoneIndex));

    return {
      type: "ResultsNode",
      resultsNode: verticalSplitNode,
    }
  }
  
  makeZoneSpaceBreakdownChart(outputs, zoneIndex) {
    let zone = this.system.getZoneAtIndex(zoneIndex);
    let zoneResult = this.zoneResults.getZoneResult(zoneIndex);
    let zoneTotals = zoneResult.getZoneTotals();
    
    // Find the month and hour with the peak cooling load for this zone
    let peakInfo = findMaxLoadInMatrix(zoneTotals.cooling.q_total);
    let peakMonth = peakInfo.time[0];
    let peakHour = peakInfo.time[1];
    
    // Create data for the stacked bar chart
    let spaceLabels = [];
    let sensibleValues = [];
    let latentValues = [];
    
    // Get data for each space in the zone
    this.system.forEachSpaceInZone(zoneIndex, (space) => {
      let spaceType = space.spaceType;
      let spaceResult = this.zoneResults.getSpaceResult(zoneIndex, space.spaceIndex);
      let spaceLoadResults = spaceResult.getLoadResults();
      
      // Get the space's load at the zone's peak time
      let sensibleLoad = spaceLoadResults.cooling.q_sensible.get([peakMonth, peakHour]);
      let latentLoad = spaceLoadResults.cooling.q_latent.get([peakMonth, peakHour]);
      
      // Multiply by quantity
      sensibleLoad *= space.quantity;
      latentLoad *= space.quantity;
      
      // Add to arrays
      spaceLabels.push(spaceType.name.value);
      sensibleValues.push(Units.convertValue(sensibleLoad, Units.Load, Units.LoadMBH));
      latentValues.push(Units.convertValue(latentLoad, Units.Load, Units.LoadMBH));
    });
    
    // Create stacked bar chart data
    let stackedBarData = {
      title: `Space Breakdown at Peak Zone Load - ${getShortMonthName(peakMonth)} ${this.ctx.toplevelData.dayOfMonth}, ${getHourString(peakHour)}`,
      units: Units.LoadMBH,
      precision: 1,
      labelX: 'Space',
      labelY: `Load`,
      labels: spaceLabels,
      datasets: [
        {
          label: 'Sensible',
          data: sensibleValues,
          color: SENSIBLE_COLOR
        },
        {
          label: 'Latent',
          data: latentValues,
          color: LATENT_COLOR
        }
      ],
      maxWidth: 800,
      height: 500
    };
    
    let chartNode = new StackedBarChartNode(stackedBarData);
    chartNode.data.helpText = `This chart shows the breakdown of cooling loads by space at the time of peak zone load for ${zone.name.value}. ` +
      `The peak zone load occurs on ${getShortMonthName(peakMonth)} ${this.ctx.toplevelData.dayOfMonth} at ${getHourString(peakHour)}.`;
    
    return chartNode;
  }
  
  makeZoneSpaceSunburstChart(outputs, zoneIndex) {
    let zone = this.system.getZoneAtIndex(zoneIndex);
    let zoneResult = this.zoneResults.getZoneResult(zoneIndex);
    let zoneTotals = zoneResult.getZoneTotals();
    
    // Find the month and hour with the peak cooling load for this zone
    let peakInfo = findMaxLoadInMatrix(zoneTotals.cooling.q_total);
    let peakMonth = peakInfo.time[0];
    let peakHour = peakInfo.time[1];
    
    // Create data structure for sunburst chart
    let sunburstData = {
      Spaces: {
        label: 'Spaces',
        value: 0,
        children: {},
      }
    };
    
    // Get data for each space in the zone
    this.system.forEachSpaceInZone(zoneIndex, (space) => {
      let spaceType = space.spaceType;
      let spaceResult = this.zoneResults.getSpaceResult(zoneIndex, space.spaceIndex);
      let spaceLoadResults = spaceResult.getLoadResults();
      
      // Get the space's load at the zone's peak time
      let sensibleLoad = spaceLoadResults.cooling.q_sensible.get([peakMonth, peakHour]);
      let latentLoad = spaceLoadResults.cooling.q_latent.get([peakMonth, peakHour]);
      
      // Multiply by quantity
      sensibleLoad *= space.quantity;
      latentLoad *= space.quantity;
      let totalLoad = sensibleLoad + latentLoad;
      
      // Convert to MBH for display
      let sensibleMBH = Units.convertValue(sensibleLoad, Units.Load, Units.LoadMBH);
      let latentMBH = Units.convertValue(latentLoad, Units.Load, Units.LoadMBH);
      let totalMBH = Units.convertValue(totalLoad, Units.Load, Units.LoadMBH);
      
      // Add to sunburst data
      let spaceNode = {
        label: `${spaceType.name.value} x${space.quantity}`,
        value: totalMBH,
        children: {
          Sensible: {
            label: "Sensible",
            value: sensibleMBH
          },
          Latent: {
            label: "Latent",
            value: latentMBH
          }
        }
      };
      
      sunburstData.Spaces.children[`Space${space.spaceIndex}`] = spaceNode;
      sunburstData.Spaces.value += totalMBH;
    });

    // Format the spaces total:
    sunburstData.Spaces.value = sunburstData.Spaces.value;
    
    // Create sunburst chart data
    let chartData = {
      title: `Loads Breakdown at Peak - ${getShortMonthName(peakMonth)} ${this.ctx.toplevelData.dayOfMonth}, ${getHourString(peakHour)}`,
      units: Units.LoadMBH,
      precision: 1,
      includeTable: true,
      data: sunburstData
    };
    
    let sunburstNode = new SunburstNode(chartData);
    sunburstNode.data.helpText = `This chart shows the breakdown of cooling loads by space at the time of zone peak load (` +
      `${getShortMonthName(peakMonth)} ${this.ctx.toplevelData.dayOfMonth} at ${getHourString(peakHour)}).`;
    
    return sunburstNode;
  }
  
  makeZoneCoolingLoadsByMonthNode(outputs, zoneIndex) {
    let zone = this.system.getZoneAtIndex(zoneIndex);
    let zoneResult = this.zoneResults.getZoneResult(zoneIndex);
    let zoneTotals = zoneResult.getZoneTotals();
    
    let monthLabels = Array.from({length: 12}, (_, i) => getShortMonthName(i));
    let sensibleValues = [];
    let latentValues = [];
    let peakTimes = [];
    
    for (let monthIndex = 0; monthIndex < 12; ++monthIndex) {
      let peakInfo = findMaxLoadForMonth(zoneTotals.cooling.q_total, monthIndex);
      let peakTime = peakInfo.time;
      peakTimes.push(peakTime);
      
      // Get sensible and latent values at the peak total load time
      let sensibleValue = zoneTotals.cooling.q_sensible.get(peakTime);
      let latentValue = zoneTotals.cooling.q_latent.get(peakTime);
      
      sensibleValues.push(Units.convertValue(sensibleValue, Units.Load, Units.LoadMBH));
      latentValues.push(Units.convertValue(latentValue, Units.Load, Units.LoadMBH));
    }
    
    // Create stacked bar chart data
    let stackedBarData = {
      title: `Peak Cooling Loads by Month`,
      units: Units.LoadMBH,
      precision: 1,
      labelX: 'Month',
      labelY: `Peak Load`,
      labels: monthLabels,
      datasets: [
        {
          label: 'Sensible',
          data: sensibleValues,
          color: SENSIBLE_COLOR
        },
        {
          label: 'Latent',
          data: latentValues,
          color: LATENT_COLOR
        }
      ],
      maxWidth: 800,
      height: 500
    };
    
    let chartNode = new StackedBarChartNode(stackedBarData);
    chartNode.data.helpText = `This chart shows the peak zone cooling load for each month for ${zone.name.value}. ` +
      `The peak load is the maximum cooling load of any hour on the design day for that month (typically the 21st).`;
    return chartNode;
  }
  
  makeZoneCoolingLoadsByHourNode(outputs, zoneIndex) {
    let zone = this.system.getZoneAtIndex(zoneIndex);
    let zoneResult = this.zoneResults.getZoneResult(zoneIndex);
    let zoneTotals = zoneResult.getZoneTotals();
    
    // Find the month with the peak cooling load
    let peakInfo = findMaxLoadInMatrix(zoneTotals.cooling.q_total);
    let peakMonth = peakInfo.time[0];
    let hourLabels = Array.from({length: 24}, (_, i) => getHourString(i));
    
    // Extract sensible and latent values for each hour of the peak day
    let sensibleValues = [];
    let latentValues = [];
    
    for (let hourIndex = 0; hourIndex < 24; ++hourIndex) {
      let sensibleValue = zoneTotals.cooling.q_sensible.get([peakMonth, hourIndex]);
      let latentValue = zoneTotals.cooling.q_latent.get([peakMonth, hourIndex]);
      
      sensibleValues.push(Units.convertValue(sensibleValue, Units.Load, Units.LoadMBH));
      latentValues.push(Units.convertValue(latentValue, Units.Load, Units.LoadMBH));
    }
    
    // Create stacked bar chart data
    let stackedBarData = {
      title: `Cooling Loads on Peak Day - ${getShortMonthName(peakMonth)} ${this.ctx.toplevelData.dayOfMonth}`,
      units: Units.LoadMBH,
      precision: 1,
      labelX: 'Hour',
      labelY: `Load`,
      labels: hourLabels,
      datasets: [
        {
          label: 'Sensible',
          data: sensibleValues,
          color: SENSIBLE_COLOR
        },
        {
          label: 'Latent',
          data: latentValues,
          color: LATENT_COLOR
        }
      ],
      maxWidth: 800,
      height: 500
    };
    
    let chartNode = new StackedBarChartNode(stackedBarData);
    chartNode.data.helpText = `This chart shows the hourly zone cooling loads on the day of peak cooling load for ${zone.name.value}, ` +
      `${getShortMonthName(peakMonth)} ${this.ctx.toplevelData.dayOfMonth}.`;
    
    return chartNode;
  }
  
  makeZoneTotalCoolingLoadMonthHourGrid(outputs, zoneIndex) {
    let zone = this.system.getZoneAtIndex(zoneIndex);
    let zoneResult = this.zoneResults.getZoneResult(zoneIndex);
    let zoneTotals = zoneResult.getZoneTotals();
    
    // Extract the total cooling load values from the matrix
    let coolingMatrix = zoneTotals.cooling.q_total;
    let gridValues = [];
    
    // Convert the matrix to a 2D array for the grid component
    for (let month = 0; month < 12; month++) {
      let monthRow = [];
      for (let hour = 0; hour < 24; hour++) {
        // Convert to MBH for better readability with one decimal place
        let value = coolingMatrix.get([month, hour]);
        value = Units.convertValue(value, Units.Load, Units.LoadMBH);
        monthRow.push(value);
      }
      gridValues.push(monthRow);
    }
    
    // Create the MonthHourGrid node
    let gridNodeData = {
      title: `Total Cooling Loads`,
      gridValues: gridValues,
      units: Units.LoadMBH,
      precision: 0,
      helpText: `This grid shows the total zone cooling loads (sensible + latent) for ${zone.name.value} for each hour of the test day for each month (typically the 21st).`
    };
    let gridNode = new MonthHourGridNode(gridNodeData);
    
    return gridNode;
  }

  makeZoneResultsSection(outputs, zoneIndex) {
    let zone = this.system.getZoneAtIndex(zoneIndex);
    let zoneResultsNode = this.makeZoneResultsNode(outputs, zoneIndex);

    return {
      name: zone.name.value,
      summaryPage: [
        zoneResultsNode
      ]
    }
  }

  makeZoneLoadsSummary(outputs) {
    let generalSection = []
    let zoneLoadsBreakdown = this.makeZoneLoadsBreakdownTable(outputs);
    generalSection.push(zoneLoadsBreakdown);
    let spaceLoadsBreakdown = this.makeSpaceLoadsBreakdownTable(outputs);
    generalSection.push(spaceLoadsBreakdown);
    let spaceVentilations = this.makeSpaceVentilationsTable(outputs);
    generalSection.push(spaceVentilations);

    let zoneSections = []
    this.system.forEachZone((zone) => {
      let zoneSection = this.makeZoneResultsSection(outputs, zone.zoneIndex);
      zoneSections.push(zoneSection);
    })

    return {
      generalSection: generalSection,
      zoneLoads: [zoneLoadsBreakdown],
      spaceLoads: [spaceLoadsBreakdown],
      spaceVentilations: [spaceVentilations],
      zoneSections: zoneSections,
    }
  }

  makeAnnualLoadsSummary(outputs) {
    // Create a vertical split node to contain all three grids
    let verticalSplitNode = new ResultsNode(ResultsNodeType.VerticalSplit, 'VerticalSplit');
    verticalSplitNode.children.push(this.makeTotalCoolingLoadMonthHourGrid(outputs))
    verticalSplitNode.children.push(this.makeSensibleCoolingLoadMonthHourGrid(outputs))
    verticalSplitNode.children.push(this.makeLatentCoolingLoadMonthHourGrid(outputs))

    return [{
      type: "ResultsNode",
      resultsNode: verticalSplitNode,
    }];
  }
  
  makeTotalCoolingLoadMonthHourGrid(outputs, opts) {
    opts = valOr(opts, {});

    // Extract the total cooling load values from the matrix
    let coolingMatrix = outputs.system_cooling.q_cooling_total;
    let gridValues = [];
    
    // Convert the matrix to a 2D array for the grid component
    for (let month = 0; month < 12; month++) {
      let monthRow = [];
      for (let hour = 0; hour < 24; hour++) {
        // Convert to MBH for better readability
        let value = coolingMatrix.get([month, hour]);
        value = Units.convertValue(value, Units.Load, Units.LoadMBH);
        monthRow.push(value);
      }
      gridValues.push(monthRow);
    }
    
    // Create the MonthHourGrid node
    let chartName = valOr(opts.name, 'Total Cooling Loads');
    let gridNodeData = {
      title: chartName,
      gridValues: gridValues,
      units: Units.LoadMBH,
      precision: 0,
      helpText: "This grid shows the total system cooling loads (sensible + latent) for each hour of the test day for each month (typically the 21st)."
    };
    let gridNode = new MonthHourGridNode(gridNodeData);
    
    return gridNode;
  }
  
  makeSensibleCoolingLoadMonthHourGrid(outputs) {
    // Extract the sensible cooling load values from the matrix
    let coolingMatrix = outputs.system_cooling.q_cooling_sens;
    let gridValues = [];
    
    // Convert the matrix to a 2D array for the grid component
    for (let month = 0; month < 12; month++) {
      let monthRow = [];
      for (let hour = 0; hour < 24; hour++) {
        // Convert to MBH for better readability
        let value = coolingMatrix.get([month, hour]);
        value = Units.convertValue(value, Units.Load, Units.LoadMBH);
        monthRow.push(value);
      }
      gridValues.push(monthRow);
    }
    
    // Create the MonthHourGrid node
    let gridNodeData = {
      title: "Sensible Cooling Loads",
      gridValues: gridValues,
      units: Units.LoadMBH,
      precision: 0,
      helpText: "This grid shows the sensible system cooling loads for each hour of the test day for each month (typically the 21st)."
    };
    let gridNode = new MonthHourGridNode(gridNodeData);
    
    return gridNode;
  }
  
  makeLatentCoolingLoadMonthHourGrid(outputs) {
    // Extract the latent cooling load values from the matrix
    let coolingMatrix = outputs.system_cooling.q_cooling_lat;
    let gridValues = [];
    
    // Convert the matrix to a 2D array for the grid component
    for (let month = 0; month < 12; month++) {
      let monthRow = [];
      for (let hour = 0; hour < 24; hour++) {
        // Convert to MBH for better readability
        let value = coolingMatrix.get([month, hour]);
        value = Units.convertValue(value, Units.Load, Units.LoadMBH);
        monthRow.push(value);
      }
      gridValues.push(monthRow);
    }
    
    // Create the MonthHourGrid node
    let gridNodeData = {
      title: "Latent Cooling Loads",
      gridValues: gridValues,
      units: Units.LoadMBH,
      precision: 0,
      helpText: "This grid shows the latent system cooling loads for each hour of the test day for each month (typically the 21st)."
    };
    let gridNode = new MonthHourGridNode(gridNodeData);
    
    return gridNode;
  }

  makeLoadCharts(outputs) { 
    let systemResultsNodeSection = {
      name: "System Load Charts",
      showName: false,
      type: "ResultsNode",
      resultsNode: this.makeSystemResultsNode(outputs),
    }
    return {
      allSections: [
        systemResultsNodeSection,
      ]
    }
  }

  makeSpaceResultsNodes() {
    let resultsNodes = []
    this.system.forEachZone((zone) => {
      let zoneResults = {
        name: zone.zone.name.value,
        spaces: [],
      }
      this.system.forEachSpaceInZone(zone.zoneIndex, (space) => {
        let spaceResults = this.getSpaceLoadResults(zone.zoneIndex, space.spaceIndex)
        zoneResults.spaces.push({
          spaceName: space.spaceType.name.value,
          resultsNode: spaceResults.resultsNode,
        })
      })
      resultsNodes.push(zoneResults)
    })
    //console.log("SPACE RESULTS NODES", resultsNodes)
    return resultsNodes;
  }

  makeSpaceBreakdownTables() {
    /**
     * Create detailed breakdown tables for each space showing all building envelope components
     */
    let breakdownTables = [];
    
    this.system.forEachZone((zone) => {
      this.system.forEachSpaceInZone(zone.zoneIndex, (space) => {
        let spaceLoadResults = this.getSpaceLoadResults(zone.zoneIndex, space.spaceIndex)
        
        // Get peak cooling time for this space
        let coolingResults = spaceLoadResults.cooling;
        let heatingResults = spaceLoadResults.heating;
        let peakCoolingInfo = findMaxLoadInMatrix(coolingResults.q_total);
        let peakTime = peakCoolingInfo.time;

        let spaceType = space.spaceType;

        //console.log(`SPACE ${space.spaceIndex} LOAD RESULTS`, spaceLoadResults);
        let tableData = [];

        // Walls
        let wallsCoolingBreakdown = coolingResults.SensibleLoads.wallLoads.Breakdown;
        let wallsHeatingBreakdown = heatingResults.SensibleLoads.wallLoads.Breakdown;
        // Windows
        let windowsCoolingBreakdown = coolingResults.SensibleLoads.windowLoads.Breakdown;
        let windowsHeatingBreakdown = heatingResults.SensibleLoads.windowLoads.Breakdown;
        // Doors
        let doorsCoolingBreakdown = coolingResults.SensibleLoads.doorLoads.Breakdown;
        let doorsHeatingBreakdown = heatingResults.SensibleLoads.doorLoads.Breakdown;
        // Roofs
        let roofsCoolingBreakdown = coolingResults.SensibleLoads.roofLoads.Breakdown;
        let roofsHeatingBreakdown = heatingResults.SensibleLoads.roofLoads.Breakdown;
        // Skylights
        let skylightsCoolingBreakdown = coolingResults.SensibleLoads.skylightLoads.Breakdown;
        let skylightsHeatingBreakdown = heatingResults.SensibleLoads.skylightLoads.Breakdown;

        let iterateCollection = (coolingBreakdown, heatingBreakdown, label) => {
          console.log(`ITERATING ${label}s`, coolingBreakdown, heatingBreakdown);
          for (const [key, item] of Object.entries(coolingBreakdown)) {
            let heatingData = heatingBreakdown[key];
            console.log(`- ${key}:`, item, heatingData);
            let area = valOr(item.metadata?.area, -1.0);
            let uValue = valOr(item.metadata?.uValue, -1.0);
            let shgc = valOr(item.metadata?.shgc, -1.0);
            let coolingNonSolar = null;
            let coolingSolar = null;
            // Windows, doors, skylights have solar and non-solar components. Walls, roofs do not.
            if (item.children) {
              coolingNonSolar = item.children.nonSolar.value.get(peakTime);
              coolingSolar = item.children.solar.value.get(peakTime);
            } else {
              coolingNonSolar = item.value.get(peakTime);
              coolingSolar = -1.0;
            }
            console.log(`  Area: ${area}, U-Value: ${uValue}, SHGC: ${shgc}, Cooling Non-Solar: ${coolingNonSolar}, Cooling Solar: ${coolingSolar}, Heating: ${heatingData.value}`);
            tableData.push({
              rowData: [
                item.label,
                OutputValue.createSimple("Area", FieldType.Area, area, {isNA: area < 0, precision: 0}),
                OutputValue.createSimple("U-Value", FieldType.UValue, uValue, {isNA: uValue < 0}),
                OutputValue.createSimple("SHGC", FieldType.Ratio, shgc, {isNA: shgc < 0}),
                OutputValue.createSimple("Cooling (Non-solar)", FieldType.Load, coolingNonSolar, {precision: 0}),
                OutputValue.createSimple("Cooling (Solar)", FieldType.Load, coolingSolar, {isNA: coolingSolar < 0, precision: 0}),
                OutputValue.createSimple("Heating", FieldType.Load, heatingData.value, {precision: 0}),
              ]
            });
          }
        };

        // Walls
        iterateCollection(wallsCoolingBreakdown, wallsHeatingBreakdown, "WALL");
        // Windows
        iterateCollection(windowsCoolingBreakdown, windowsHeatingBreakdown, "WINDOW");
        // Doors
        iterateCollection(doorsCoolingBreakdown, doorsHeatingBreakdown, "DOOR");
        // Roofs
        iterateCollection(roofsCoolingBreakdown, roofsHeatingBreakdown, "ROOF");
        // Skylights
        iterateCollection(skylightsCoolingBreakdown, skylightsHeatingBreakdown, "SKYLIGHT");
        // Partitions
        let partitionsCoolingBreakdown = coolingResults.SensibleLoads.partitionLoads.Breakdown;
        let partitionsHeatingBreakdown = heatingResults.SensibleLoads.partitionLoads.Breakdown;
        iterateCollection(partitionsCoolingBreakdown, partitionsHeatingBreakdown, "PARTITION");
        
        let breakdownTable = {
          name: `${spaceType.name.value} - Envelope Breakdown`,
          type: "Table",
          columns: [
            {name: "Component"},
            {name: "Area"},
            {name: "U-Value"},
            {name: "SHGC"},
            {name: "Cooling (Non-solar)"},
            {name: "Cooling (Solar)"},
            {name: "Heating"},
          ],
          data: tableData,
        };
        
        breakdownTables.push(breakdownTable);
      })
    });
    
    return breakdownTables;
  }

  makeSpaceResultsData() {
    /**
     * For each space, create an object with rows of data, where each row has:
     * - label: string description
     * - coolingDetails: area/power details for cooling
     * - coolingSensible: sensible cooling load (BTU/hr)
     * - coolingLatent: latent cooling load (BTU/hr)
     * - heatingDetails: area/power details for heating
     * - heatingSensible: sensible heating load (BTU/hr)
     */
    let resultsData = []
    this.system.forEachZone((zone) => {
      this.system.forEachSpaceInZone(zone.zoneIndex, (space) => {
        let spaceResults = this.getSpaceLoadResults(zone.zoneIndex, space.spaceIndex)
        let spaceType = space.spaceType;
        
        // Get peak cooling time for this space
        let coolingResults = spaceResults.cooling;
        let heatingResults = spaceResults.heating;
        let peakCoolingInfo = findMaxLoadInMatrix(coolingResults.q_total);
        let peakTime = peakCoolingInfo.time;
        
        let sensibleLoads = coolingResults.SensibleLoads;
        let latentLoads = coolingResults.LatentLoads;
        let heatingSensibleLoads = heatingResults.SensibleLoads;

        let windowSolarDetails = Quantity.create(spaceType.getTotalWindowArea(), Units.ft2, {
          precision: 0
        });
        let skylightSolarDetails = Quantity.create(spaceType.getTotalSkylightArea(), Units.ft2, {
          precision: 0
        });
        let wallTransmissionDetails = Quantity.create(spaceType.getTotalWallAreaMinusOpenings(), Units.ft2, {
          precision: 0
        });
        let roofTransmissionDetails = Quantity.create(spaceType.getTotalRoofAreaMinusOpenings(), Units.ft2, {
          precision: 0
        });
        let windowTransmissionDetails = Quantity.create(spaceType.getTotalWindowArea(), Units.ft2, {
          precision: 0
        });
        let skylightTransmissionDetails = Quantity.create(spaceType.getTotalSkylightArea(), Units.ft2, {
          precision: 0
        });
        let doorLoadsDetails = Quantity.create(spaceType.getTotalDoorArea(), Units.ft2, {
          precision: 0
        });
        let floorTransmissionDetails = Quantity.create(spaceType.getFloorArea(), Units.ft2, {
          precision: 0
        });
        let partitionDetails = Quantity.create(spaceType.getTotalPartitionArea(), Units.ft2, {
          precision: 0
        });
        let peopleDetails = Quantity.create(spaceType.getNumOccupants(), Units.None, {
          precision: 0
        });
        let dataRows = [
          {
            label: "Window Solar Loads",
            details: windowSolarDetails,
            coolingSensible: sensibleLoads.windowLoads.SolarLoadsMatrix.get(peakTime),
            coolingLatent: null,
            heatingSensible: null,
            heatingLatent: null,
          },
          {
            label: "Skylight Solar Loads", 
            details: skylightSolarDetails,
            coolingSensible: sensibleLoads.skylightLoads.SolarLoadsMatrix.get(peakTime),
            coolingLatent: null,
            heatingSensible: null,
            heatingLatent: null,
          },
          {
            label: "Wall Transmission",
            details: wallTransmissionDetails,
            coolingSensible: sensibleLoads.wallLoads.SensibleLoadsMatrix.get(peakTime),
            coolingLatent: null,
            heatingSensible: heatingSensibleLoads.wallLoads.SensibleLoadValue,
            heatingLatent: null,
          },
          {
            label: "Roof Transmission",
            details: roofTransmissionDetails,
            coolingSensible: sensibleLoads.roofLoads.SensibleLoadsMatrix.get(peakTime),
            coolingLatent: null,
            heatingSensible: heatingSensibleLoads.roofLoads.SensibleLoadValue,
            heatingLatent: null,
          },
          {
            label: "Window Transmission",
            details: windowTransmissionDetails,
            coolingSensible: sensibleLoads.windowLoads.NonSolarLoadsMatrix.get(peakTime),
            coolingLatent: null,
            heatingSensible: heatingSensibleLoads.windowLoads.SensibleLoadValue,
            heatingLatent: null,
          },
          {
            label: "Skylight Transmission",
            details: skylightTransmissionDetails,
            coolingSensible: sensibleLoads.skylightLoads.NonSolarLoadsMatrix.get(peakTime),
            coolingLatent: null,
            heatingSensible: heatingSensibleLoads.skylightLoads.SensibleLoadValue,
            heatingLatent: null,
          },
          {
            label: "Door Loads",
            details: doorLoadsDetails,
            coolingSensible: sensibleLoads.doorLoads.SensibleLoadsMatrix.get(peakTime),
            coolingLatent: null,
            heatingSensible: heatingSensibleLoads.doorLoads.SensibleLoadValue,
            heatingLatent: null,
          },
          {
            label: "Floor Transmission",
            details: floorTransmissionDetails,
            coolingSensible: sensibleLoads.floorLoads.SensibleLoadsMatrix.get(peakTime),
            coolingLatent: null,
            heatingSensible: heatingSensibleLoads.floorLoads.SensibleLoadValue,
            heatingLatent: null,
          },
          {
            label: "Partitions",
            details: partitionDetails,
            coolingSensible: sensibleLoads.partitionLoads.SensibleLoadsMatrix.get(peakTime),
            coolingLatent: null,
            heatingSensible: heatingSensibleLoads.partitionLoads.SensibleLoadValue,
            heatingLatent: null,
          },
          {
            label: "Lighting",
            //details: `${spaceType.getTotalLightingPower()} W`,
            details: null,
            coolingSensible: sensibleLoads.lightingLoads.SensibleLoadsMatrix.get(peakTime),
            coolingLatent: null,
            heatingSensible: null,
            heatingLatent: null,
          },
          {
            label: "Appliances",
            //details: `${spaceType.getTotalAppliancePower()} W`,
            details: null,
            coolingSensible: sensibleLoads.applianceLoads.SensibleLoadsMatrix.get(peakTime),
            coolingLatent: latentLoads.applianceLoads.LatentLoadsMatrix.get(peakTime),
            heatingSensible: null,
            heatingLatent: null,
          },
          {
            label: "People",
            details: peopleDetails,
            coolingSensible: sensibleLoads.peopleLoads.SensibleLoadsMatrix.get(peakTime),
            coolingLatent: latentLoads.peopleLoads.LatentLoadsMatrix.get(peakTime),
            heatingSensible: null,
            heatingLatent: null,
          },
          {
            label: "Infiltration",
            details: null,
            coolingSensible: sensibleLoads.infiltrationLoads.SensibleLoadsMatrix.get(peakTime),
            coolingLatent: latentLoads.infiltrationLoads.LatentLoadsMatrix.get(peakTime),
            heatingSensible: heatingSensibleLoads.infiltrationLoads.SensibleLoadValue,
            heatingLatent: null,
          },
          {
            label: "Miscellaneous",
            details: null,
            coolingSensible: sensibleLoads.miscLoads.SensibleLoadsMatrix.get(peakTime),
            coolingLatent: latentLoads.miscLoads.LatentLoadsMatrix.get(peakTime),
            heatingSensible: null,
            heatingLatent: null,
          }
        ];

        // Calculate totals
        let valOrZero = (val) => val || 0;
        let totalCoolingSensible = dataRows.reduce((sum, row) => sum + valOrZero(row.coolingSensible), 0);
        let totalCoolingLatent = dataRows.reduce((sum, row) => sum + valOrZero(row.coolingLatent), 0);
        let totalHeatingSensible = dataRows.reduce((sum, row) => sum + valOrZero(row.heatingSensible), 0);

        let coolingPeakTime = getMonthHourString(peakTime[0], peakTime[1]);
        let spaceResult = {
          spaceName: space.spaceType.name.value,
          spaceDesc: `Space "${space.spaceType.name.value}" in Zone "${zone.zone.name.value}"`,
          loadUnits: Units.Load,
          dataRows: dataRows,
          quantity: space.quantity,
          heatingResultsNode: spaceResults.heating.sunburstNode,
          coolingResultsNode: spaceResults.cooling.sunburstNode,
          designInfo: {
            coolingPeakTime: coolingPeakTime,
            coolingOutdoorTempDB: Quantity.create(this.calc_T_db_out_cooling(peakTime[1], peakTime[0]), Units.F),
            coolingOutdoorTempWB: Quantity.create(this.calc_T_wb_out_cooling(peakTime[1], peakTime[0]), Units.F),
            coolingIndoorTemp: Quantity.create(zone.zone.summerIndoorTemp.stdValue, Units.F),
            heatingOutdoorTempDB: Quantity.create(this.calc_T_db_out_heating(), Units.F),
            heatingIndoorTemp: Quantity.create(zone.zone.winterIndoorTemp.stdValue, Units.F),
          },
          total: {
            coolingSensible: totalCoolingSensible,
            coolingLatent: totalCoolingLatent,
            heatingSensible: totalHeatingSensible,
            heatingLatent: null
          },
          zoneTotals: {
            coolingSensible: totalCoolingSensible * space.quantity,
            coolingLatent: totalCoolingLatent * space.quantity,
            heatingSensible: totalHeatingSensible * space.quantity,
            heatingLatent: null,
          },
        }

        resultsData.push(spaceResult)
      })
    })
    return resultsData;
  }

  makeZoneResultsData() {
    /**
     * For each zone, create an object with rows of data aggregated across all spaces in the zone.
     * Same format as makeSpaceResultsData, but uses zone peak cooling time and sums loads
     * (scaled by space quantity) across all spaces in the zone.
     */
    let resultsData = [];
    this.system.forEachZone((zone) => {
      let zoneResult = this.zoneResults.getZoneResult(zone.zoneIndex);
      let zoneTotals = zoneResult.getZoneTotals();

      // Use zone-level peak cooling time
      let peakCoolingInfo = findMaxLoadInMatrix(zoneTotals.cooling.q_total);
      let peakTime = peakCoolingInfo.time;

      // Aggregate areas and loads across all spaces, scaled by quantity
      let totalWindowSolarArea = 0, totalSkylightSolarArea = 0;
      let totalWallArea = 0, totalRoofArea = 0;
      let totalWindowArea = 0, totalSkylightArea = 0;
      let totalDoorArea = 0, totalFloorArea = 0;
      let totalPartitionArea = 0, totalOccupants = 0;

      let windowSolar = 0, skylightSolar = 0;
      let wallTrans = 0, roofTrans = 0;
      let windowTrans = 0, skylightTrans = 0;
      let doorLoads = 0, floorTrans = 0, partitions = 0;
      let lightingSensible = 0;
      let appliancesSensible = 0, appliancesLatent = 0;
      let peopleSensible = 0, peopleLatent = 0;
      let infiltrationSensible = 0, infiltrationLatent = 0;
      let miscSensible = 0, miscLatent = 0;

      let heatingWall = 0, heatingRoof = 0;
      let heatingWindow = 0, heatingSkylight = 0;
      let heatingDoor = 0, heatingFloor = 0;
      let heatingPartition = 0, heatingInfiltration = 0;

      this.system.forEachSpaceInZone(zone.zoneIndex, (space) => {
        let N = space.quantity;
        let spaceResults = this.getSpaceLoadResults(zone.zoneIndex, space.spaceIndex);
        let spaceType = space.spaceType;
        let sensibleLoads = spaceResults.cooling.SensibleLoads;
        let latentLoads = spaceResults.cooling.LatentLoads;
        let heatingSensibleLoads = spaceResults.heating.SensibleLoads;

        totalWindowSolarArea += N * spaceType.getTotalWindowArea();
        totalSkylightSolarArea += N * spaceType.getTotalSkylightArea();
        totalWallArea += N * spaceType.getTotalWallAreaMinusOpenings();
        totalRoofArea += N * spaceType.getTotalRoofAreaMinusOpenings();
        totalWindowArea += N * spaceType.getTotalWindowArea();
        totalSkylightArea += N * spaceType.getTotalSkylightArea();
        totalDoorArea += N * spaceType.getTotalDoorArea();
        totalFloorArea += N * spaceType.getFloorArea();
        totalPartitionArea += N * spaceType.getTotalPartitionArea();
        totalOccupants += N * spaceType.getNumOccupants();

        windowSolar += N * sensibleLoads.windowLoads.SolarLoadsMatrix.get(peakTime);
        skylightSolar += N * sensibleLoads.skylightLoads.SolarLoadsMatrix.get(peakTime);
        wallTrans += N * sensibleLoads.wallLoads.SensibleLoadsMatrix.get(peakTime);
        roofTrans += N * sensibleLoads.roofLoads.SensibleLoadsMatrix.get(peakTime);
        windowTrans += N * sensibleLoads.windowLoads.NonSolarLoadsMatrix.get(peakTime);
        skylightTrans += N * sensibleLoads.skylightLoads.NonSolarLoadsMatrix.get(peakTime);
        doorLoads += N * sensibleLoads.doorLoads.SensibleLoadsMatrix.get(peakTime);
        floorTrans += N * sensibleLoads.floorLoads.SensibleLoadsMatrix.get(peakTime);
        partitions += N * sensibleLoads.partitionLoads.SensibleLoadsMatrix.get(peakTime);
        lightingSensible += N * sensibleLoads.lightingLoads.SensibleLoadsMatrix.get(peakTime);
        appliancesSensible += N * sensibleLoads.applianceLoads.SensibleLoadsMatrix.get(peakTime);
        appliancesLatent += N * latentLoads.applianceLoads.LatentLoadsMatrix.get(peakTime);
        peopleSensible += N * sensibleLoads.peopleLoads.SensibleLoadsMatrix.get(peakTime);
        peopleLatent += N * latentLoads.peopleLoads.LatentLoadsMatrix.get(peakTime);
        infiltrationSensible += N * sensibleLoads.infiltrationLoads.SensibleLoadsMatrix.get(peakTime);
        infiltrationLatent += N * latentLoads.infiltrationLoads.LatentLoadsMatrix.get(peakTime);
        miscSensible += N * sensibleLoads.miscLoads.SensibleLoadsMatrix.get(peakTime);
        miscLatent += N * latentLoads.miscLoads.LatentLoadsMatrix.get(peakTime);

        heatingWall += N * heatingSensibleLoads.wallLoads.SensibleLoadValue;
        heatingRoof += N * heatingSensibleLoads.roofLoads.SensibleLoadValue;
        heatingWindow += N * heatingSensibleLoads.windowLoads.SensibleLoadValue;
        heatingSkylight += N * heatingSensibleLoads.skylightLoads.SensibleLoadValue;
        heatingDoor += N * heatingSensibleLoads.doorLoads.SensibleLoadValue;
        heatingFloor += N * heatingSensibleLoads.floorLoads.SensibleLoadValue;
        heatingPartition += N * heatingSensibleLoads.partitionLoads.SensibleLoadValue;
        heatingInfiltration += N * heatingSensibleLoads.infiltrationLoads.SensibleLoadValue;
      });

      let dataRows = [
        {
          label: "Window Solar Loads",
          details: Quantity.create(totalWindowSolarArea, Units.ft2, { precision: 0 }),
          coolingSensible: windowSolar,
          coolingLatent: null,
          heatingSensible: null,
          heatingLatent: null,
        },
        {
          label: "Skylight Solar Loads",
          details: Quantity.create(totalSkylightSolarArea, Units.ft2, { precision: 0 }),
          coolingSensible: skylightSolar,
          coolingLatent: null,
          heatingSensible: null,
          heatingLatent: null,
        },
        {
          label: "Wall Transmission",
          details: Quantity.create(totalWallArea, Units.ft2, { precision: 0 }),
          coolingSensible: wallTrans,
          coolingLatent: null,
          heatingSensible: heatingWall,
          heatingLatent: null,
        },
        {
          label: "Roof Transmission",
          details: Quantity.create(totalRoofArea, Units.ft2, { precision: 0 }),
          coolingSensible: roofTrans,
          coolingLatent: null,
          heatingSensible: heatingRoof,
          heatingLatent: null,
        },
        {
          label: "Window Transmission",
          details: Quantity.create(totalWindowArea, Units.ft2, { precision: 0 }),
          coolingSensible: windowTrans,
          coolingLatent: null,
          heatingSensible: heatingWindow,
          heatingLatent: null,
        },
        {
          label: "Skylight Transmission",
          details: Quantity.create(totalSkylightArea, Units.ft2, { precision: 0 }),
          coolingSensible: skylightTrans,
          coolingLatent: null,
          heatingSensible: heatingSkylight,
          heatingLatent: null,
        },
        {
          label: "Door Loads",
          details: Quantity.create(totalDoorArea, Units.ft2, { precision: 0 }),
          coolingSensible: doorLoads,
          coolingLatent: null,
          heatingSensible: heatingDoor,
          heatingLatent: null,
        },
        {
          label: "Floor Transmission",
          details: Quantity.create(totalFloorArea, Units.ft2, { precision: 0 }),
          coolingSensible: floorTrans,
          coolingLatent: null,
          heatingSensible: heatingFloor,
          heatingLatent: null,
        },
        {
          label: "Partitions",
          details: Quantity.create(totalPartitionArea, Units.ft2, { precision: 0 }),
          coolingSensible: partitions,
          coolingLatent: null,
          heatingSensible: heatingPartition,
          heatingLatent: null,
        },
        {
          label: "Lighting",
          details: null,
          coolingSensible: lightingSensible,
          coolingLatent: null,
          heatingSensible: null,
          heatingLatent: null,
        },
        {
          label: "Appliances",
          details: null,
          coolingSensible: appliancesSensible,
          coolingLatent: appliancesLatent,
          heatingSensible: null,
          heatingLatent: null,
        },
        {
          label: "People",
          details: Quantity.create(totalOccupants, Units.None, { precision: 0 }),
          coolingSensible: peopleSensible,
          coolingLatent: peopleLatent,
          heatingSensible: null,
          heatingLatent: null,
        },
        {
          label: "Infiltration",
          details: null,
          coolingSensible: infiltrationSensible,
          coolingLatent: infiltrationLatent,
          heatingSensible: heatingInfiltration,
          heatingLatent: null,
        },
        {
          label: "Miscellaneous",
          details: null,
          coolingSensible: miscSensible,
          coolingLatent: miscLatent,
          heatingSensible: null,
          heatingLatent: null,
        },
      ];

      // Calculate totals
      let valOrZero = (val) => val || 0;
      let totalCoolingSensible = dataRows.reduce((sum, row) => sum + valOrZero(row.coolingSensible), 0);
      let totalCoolingLatent = dataRows.reduce((sum, row) => sum + valOrZero(row.coolingLatent), 0);
      let totalHeatingSensible = dataRows.reduce((sum, row) => sum + valOrZero(row.heatingSensible), 0);

      let coolingPeakTime = getMonthHourString(peakTime[0], peakTime[1]);
      resultsData.push({
        zoneName: zone.zone.name.value,
        zoneDesc: `Zone "${zone.zone.name.value}"`,
        loadUnits: Units.Load,
        dataRows,
        designInfo: {
          coolingPeakTime: coolingPeakTime,
          coolingOutdoorTempDB: Quantity.create(this.calc_T_db_out_cooling(peakTime[1], peakTime[0]), Units.F),
          coolingOutdoorTempWB: Quantity.create(this.calc_T_wb_out_cooling(peakTime[1], peakTime[0]), Units.F),
          coolingIndoorTemp: Quantity.create(zone.zone.summerIndoorTemp.stdValue, Units.F),
          heatingOutdoorTempDB: Quantity.create(this.calc_T_db_out_heating(), Units.F),
          heatingIndoorTemp: Quantity.create(zone.zone.winterIndoorTemp.stdValue, Units.F),
        },
        total: {
          coolingSensible: totalCoolingSensible,
          coolingLatent: totalCoolingLatent,
          heatingSensible: totalHeatingSensible,
          heatingLatent: null,
        },
      });
    });
    return resultsData;
  }

  makeSystemResultsData(outputs) {
  }

  async calcLoads() {
    let ctx = this.ctx
    SystemLoadCalculator.setupCalcContext(ctx, this.proj, this.system)

    let outputs = await this.calcAllOutputs()

    // After calculating outputs, disable calc context logging so that
    // we don't add unnecessary log entries.
    ctx.setLoggingEnabled(false);

    let outputSummary = this.makeOutputSummary(outputs)
    let zoneLoadsSummary = this.makeZoneLoadsSummary(outputs)
    // Removed from results to keep it small
    //let annualLoadsSummary = this.makeAnnualLoadsSummary(outputs)
    let loadCharts = this.makeLoadCharts(outputs);
    let systemInputSummary = this.makeSystemInputSummary(outputs)
    let spaceInputSummary = this.makeSpaceInputSummary()
    let spaceResultsNodes = this.makeSpaceResultsNodes();
    let spaceResultsData = this.makeSpaceResultsData();
    let zoneResultsData = this.makeZoneResultsData();
    let spaceBreakdownTables = this.makeSpaceBreakdownTables();

    let debugOutputs = {
      zoneResults: outputs.zoneResults,
      Q_supply_values: outputs.Q_supply_values,
      V_ot_values: outputs.V_ot_values,
      system_cooling: outputs.system_cooling,
      system_heating: outputs.system_heating,
    }
    return {
      debugOutputs: debugOutputs,
      outputSummary: outputSummary,
      zoneLoadsSummary: zoneLoadsSummary,
      //annualLoadsSummary: annualLoadsSummary,
      loadCharts: loadCharts,
      systemInputSummary: systemInputSummary,
      spaceInputSummary: spaceInputSummary,
      spaceResultsNodes: spaceResultsNodes,
      spaceResultsData: spaceResultsData,
      zoneResultsData: zoneResultsData,
      spaceBreakdownTables: spaceBreakdownTables,
    }
  }
}
